import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import './App.css';

type CalendarView = 'month' | 'week' | 'day' | 'timeline';

type Recurrence = 'none' | 'daily' | 'weekly' | 'monthly';
type CalendarId = 'work' | 'personal' | 'team';
type ThemeId = 'light' | 'dark' | 'ocean' | 'sunset' | 'forest' | 'custom';
type RSVPStatus = 'accepted' | 'maybe' | 'declined';
type Attendee = { id: string; name: string; status: RSVPStatus };
type Attachment = { id: string; label: string; url: string };
type ChecklistItem = { id: string; text: string; done: boolean };
type EventTemplate = {
  id: string;
  name: string;
  title: string;
  description: string;
  durationMinutes?: number;
  color?: string;
  location?: string;
  attendees?: { name: string; status?: RSVPStatus }[];
  onlineLink?: string;
  recurrence?: Recurrence;
  allDay?: boolean;
};

const calendars: { id: CalendarId; label: string }[] = [
  { id: 'work', label: 'Work' },
  { id: 'personal', label: 'Personal' },
  { id: 'team', label: 'Team' },
];

type CalendarEvent = {
  id: string;
  title: string;
  description?: string;
  start: string;
  end: string;
  allDay: boolean;
  color: string;
  recurrence: Recurrence;
  recurrenceEnd?: string | null;
  exceptions?: string[];
  location?: string;
  attendees?: Attendee[];
  attachments?: Attachment[];
  checklist?: ChecklistItem[];
  meetingNotes?: string;
  onlineLink?: string;
  calendarId: CalendarId;
};

type EventWithSpan = CalendarEvent & {
  spanIndex?: number;
  spanTotal?: number;
  occurrenceKey?: string;
};

type Filters = {
  query: string;
  color: string;
  from: string;
  to: string;
  name?: string;
  calendars: CalendarId[];
  location?: string;
  attendee?: string;
  onlineLink?: string;
  heatmap?: boolean;
};

type Reminder = {
  id: string;
  eventId: string;
  at: string;
  message: string;
  dismissed?: boolean;
};

type ActivityLog = {
  id: string;
  at: string;
  message: string;
  eventId?: string;
};

type ThemeTokens = {
  bg: string;
  bgPanel: string;
  bgMuted: string;
  bgStrong: string;
  text: string;
  textMuted: string;
  accentFrom: string;
  accentTo: string;
  border: string;
  chipMuted: string;
  hover: string;
  today: string;
  shadow: string;
  shadowStrong: string;
  nowLine: string;
};

type ThemeConfig = {
  id: ThemeId;
  label: string;
  description: string;
  tokens: ThemeTokens;
};

type FormErrors = {
  title?: string;
  date?: string;
  startTime?: string;
  endTime?: string;
  timeRange?: string;
  onlineLink?: string;
};

const isValidUrl = (value: string) => {
  if (!value) return true;
  try {
    const url = new URL(value);
    return ['http:', 'https:'].includes(url.protocol);
  } catch {
    return false;
  }
};

type EventForm = {
  title: string;
  date: string;
  startTime: string;
  endTime: string;
  description: string;
  allDay: boolean;
  color: string;
  repeat: Recurrence;
  repeatUntil: string;
  location: string;
  attendees: Attendee[];
  newAttendeeName: string;
  newAttendeeStatus: RSVPStatus;
  attachments: Attachment[];
  newAttachmentLabel: string;
  newAttachmentUrl: string;
  onlineLink: string;
  meetingNotes: string;
  checklist: ChecklistItem[];
  newChecklistText: string;
  calendarId: CalendarId;
};

const daysOfWeek = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const pad = (value: number) => value.toString().padStart(2, '0');

const toInputDate = (date: Date) =>
  `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;

const addMinutesToTime = (time: string, minutes: number) => {
  const [h, m] = time.split(':').map((v) => parseInt(v || '0', 10));
  const total = Math.max(0, Math.min(23 * 60 + 59, h * 60 + (m || 0) + minutes));
  const hh = pad(Math.floor(total / 60));
  const mm = pad(total % 60);
  return `${hh}:${mm}`;
};

const addDays = (date: Date, days: number) => {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
};

const startOfWeek = (date: Date) => addDays(date, -date.getDay());

const isSameDay = (a: Date, b: Date) =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

const isSameMonth = (a: Date, b: Date) =>
  a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth();

const startOfDay = (date: Date) => new Date(date.getFullYear(), date.getMonth(), date.getDate());

const getMonthGrid = (date: Date) => {
  const start = new Date(date.getFullYear(), date.getMonth(), 1);
  const end = new Date(date.getFullYear(), date.getMonth() + 1, 0);
  const gridStart = addDays(start, -start.getDay());
  const gridEnd = addDays(end, 6 - end.getDay());
  const days: Date[] = [];

  for (let current = gridStart; current <= gridEnd; current = addDays(current, 1)) {
    days.push(new Date(current));
  }

  return days;
};

const formatMonthYear = (date: Date) =>
  date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

const formatTime = (date: Date, timeZone?: string) =>
  date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', timeZone });

const formatDate = (date: Date) => date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

const cloneEvents = (list: CalendarEvent[]) =>
  list.map((item) => ({
    ...item,
    exceptions: item.exceptions ? [...item.exceptions] : [],
    attachments: item.attachments ? [...item.attachments] : [],
    checklist: item.checklist ? item.checklist.map((c) => ({ ...c })) : [],
  }));

const nowIso = () => new Date().toISOString();

const defaultForm = (anchor: Date): EventForm => ({
  title: '',
  date: toInputDate(anchor),
  startTime: '09:00',
  endTime: '10:00',
  description: '',
  allDay: false,
  color: '#5f6af3',
  repeat: 'none',
  repeatUntil: '',
  location: '',
  attendees: [],
  newAttendeeName: '',
  newAttendeeStatus: 'accepted',
  attachments: [],
  newAttachmentLabel: '',
  newAttachmentUrl: '',
  onlineLink: '',
  meetingNotes: '',
  checklist: [],
  newChecklistText: '',
  calendarId: calendars[0].id,
});

const seededEvents: CalendarEvent[] = [
  {
    id: '1',
    title: 'Product sync',
    description: 'Align on roadmap updates',
    start: new Date().toISOString(),
    end: addDays(new Date(), 0).toISOString(),
    allDay: true,
    color: '#5f6af3',
    recurrence: 'weekly',
    recurrenceEnd: addDays(new Date(), 60).toISOString(),
    exceptions: [],
    location: 'HQ · Room A',
    attendees: [
      { id: 'a1', name: 'PMs', status: 'accepted' },
      { id: 'a2', name: 'Leads', status: 'maybe' },
    ],
    attachments: [
      { id: 'att-1', label: 'Deck', url: 'https://example.com/roadmap.pdf' },
      { id: 'att-2', label: 'Notes doc', url: 'https://example.com/notes' },
    ],
    onlineLink: 'https://meet.example.com/sync',
    calendarId: 'work',
  },
  {
    id: '2',
    title: 'Design review',
    description: 'Review new booking flow',
    start: new Date(new Date().setHours(14, 0, 0, 0)).toISOString(),
    end: new Date(new Date().setHours(15, 0, 0, 0)).toISOString(),
    allDay: false,
    color: '#1f9f83',
    recurrence: 'none',
    recurrenceEnd: null,
    exceptions: [],
    location: 'Room 4',
    attendees: [
      { id: 'a3', name: 'Designers', status: 'accepted' },
      { id: 'a4', name: 'PM', status: 'accepted' },
    ],
    onlineLink: 'https://meet.example.com/design',
    calendarId: 'work',
  },
  {
    id: '3',
    title: 'Team dinner',
    description: 'Celebrating launch',
    start: addDays(new Date(), 3).toISOString(),
    end: addDays(new Date(), 3).toISOString(),
    allDay: true,
    color: '#f2962f',
    recurrence: 'none',
    recurrenceEnd: null,
    exceptions: [],
    location: 'Downtown Bistro',
    attendees: [{ id: 'a5', name: 'Team', status: 'accepted' }],
    onlineLink: '',
    calendarId: 'team',
  },
];

const recurrenceSummary = (event: CalendarEvent) => {
  if (event.recurrence === 'none') return '';
  const parts = [`Repeats ${event.recurrence}`];
  if (event.recurrenceEnd) {
    parts.push(`until ${formatDate(new Date(event.recurrenceEnd))}`);
  }
  if (event.exceptions?.length) {
    parts.push(`skipped ${event.exceptions.length} date${event.exceptions.length > 1 ? 's' : ''}`);
  }
  return parts.join(' · ');
};

  const matchesFilters = (event: EventWithSpan, day: Date, filters: Filters) => {
    const query = filters.query.trim().toLowerCase();
    if (query) {
      const haystack = `${event.title} ${event.description ?? ''}`.toLowerCase();
      if (!haystack.includes(query)) return false;
    }

    if (filters.color && event.color !== filters.color) return false;

    if (filters.calendars && filters.calendars.length > 0 && !filters.calendars.includes(event.calendarId)) {
      return false;
    }

    if (filters.location?.trim()) {
      const loc = (event.location || '').toLowerCase();
      if (!loc.includes(filters.location.trim().toLowerCase())) return false;
    }

    if (filters.attendee?.trim()) {
      const needle = filters.attendee.trim().toLowerCase();
      const names = event.attendees?.map((a) => a.name.toLowerCase()) ?? [];
      if (!names.some((name) => name.includes(needle))) return false;
    }

    if (filters.onlineLink?.trim()) {
      const link = (event.onlineLink || '').toLowerCase();
      if (!link.includes(filters.onlineLink.trim().toLowerCase())) return false;
    }

    const dayKey = toInputDate(day);
    if (filters.from && dayKey < filters.from) return false;
    if (filters.to && dayKey > filters.to) return false;

    return true;
};

const parseAttendees = (value?: string | Attendee[]) => {
  if (!value) return [];
  if (Array.isArray(value)) return value.map((item) => item.name).filter(Boolean);
  return value
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);
};

const rsvpLabel: Record<RSVPStatus, string> = {
  accepted: 'Kabul',
  maybe: 'Belki',
  declined: 'Reddetti',
};

const rsvpICS: Record<RSVPStatus, string> = {
  accepted: 'ACCEPTED',
  maybe: 'TENTATIVE',
  declined: 'DECLINED',
};

const normalizeAttachments = (list?: Attachment[]) =>
  (list ?? []).map((att, index) => ({
    id: att.id || `att-${index}-${crypto.randomUUID()}`,
    label: att.label,
    url: att.url,
  }));

const colorPalette = ['#5f6af3', '#1f9f83', '#f2962f', '#f43f5e', '#0ea5e9'];

const sumDurations = (events: CalendarEvent[]) =>
  events.reduce((acc, event) => acc + (new Date(event.end).getTime() - new Date(event.start).getTime()), 0);

const withAlpha = (hex: string, alpha: number) => {
  const normalized = hex.replace('#', '');
  const value =
    normalized.length === 3
      ? normalized
          .split('')
          .map((c) => c + c)
          .join('')
      : normalized.padEnd(6, '0');
  const int = parseInt(value.substring(0, 6), 16);
  const r = (int >> 16) & 255;
  const g = (int >> 8) & 255;
  const b = int & 255;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const customThemeFields: { key: keyof ThemeTokens; label: string }[] = [
  { key: 'bg', label: 'Arka plan' },
  { key: 'bgPanel', label: 'Paneller' },
  { key: 'bgMuted', label: 'Muted' },
  { key: 'text', label: 'Metin' },
  { key: 'textMuted', label: 'Metin (ikincil)' },
  { key: 'accentFrom', label: 'Vurgu başlangıç' },
  { key: 'accentTo', label: 'Vurgu bitiş' },
  { key: 'border', label: 'Sınır' },
  { key: 'chipMuted', label: 'Çip arka plan' },
  { key: 'hover', label: 'Hover' },
  { key: 'today', label: 'Bugün' },
];

const themePresets: ThemeConfig[] = [
  {
    id: 'light',
    label: 'Açık',
    description: 'Parlak, sade arayüz',
    tokens: {
      bg: '#f8fafc',
      bgPanel: '#ffffff',
      bgMuted: '#f8fafc',
      bgStrong: '#0f172a',
      text: '#0f172a',
      textMuted: '#475569',
      accentFrom: '#5f6af3',
      accentTo: '#1f9f83',
      border: '#e2e8f0',
      chipMuted: '#e2e8f0',
      hover: '#eef2ff',
      today: '#eef2ff',
      shadow: 'rgba(15, 23, 42, 0.08)',
      shadowStrong: 'rgba(15, 23, 42, 0.25)',
      nowLine: '#ef4444',
    },
  },
  {
    id: 'dark',
    label: 'Koyu',
    description: 'Düşük ışık, kontrastlı',
    tokens: {
      bg: '#0b1220',
      bgPanel: '#0f172a',
      bgMuted: '#111827',
      bgStrong: '#0f172a',
      text: '#e2e8f0',
      textMuted: '#94a3b8',
      accentFrom: '#7c7bff',
      accentTo: '#35c29f',
      border: '#1f2937',
      chipMuted: '#1f2937',
      hover: '#1f2937',
      today: '#1f2937',
      shadow: 'rgba(0, 0, 0, 0.3)',
      shadowStrong: 'rgba(15, 23, 42, 0.5)',
      nowLine: '#ef4444',
    },
  },
  {
    id: 'ocean',
    label: 'Okyanus',
    description: 'Serin mavi-yeşil tonlar',
    tokens: {
      bg: '#e0f2fe',
      bgPanel: '#ffffff',
      bgMuted: '#e2f3ff',
      bgStrong: '#0b3b60',
      text: '#0b3b60',
      textMuted: '#1d4e89',
      accentFrom: '#0ea5e9',
      accentTo: '#14b8a6',
      border: '#bae6fd',
      chipMuted: '#cfe9ff',
      hover: '#dbeafe',
      today: '#c7e3ff',
      shadow: 'rgba(11, 59, 96, 0.1)',
      shadowStrong: 'rgba(11, 59, 96, 0.35)',
      nowLine: '#0ea5e9',
    },
  },
  {
    id: 'sunset',
    label: 'Gün batımı',
    description: 'Sıcak mercan, amber',
    tokens: {
      bg: '#fff3e8',
      bgPanel: '#ffffff',
      bgMuted: '#fff7ed',
      bgStrong: '#431407',
      text: '#431407',
      textMuted: '#9a3412',
      accentFrom: '#f97316',
      accentTo: '#ec4899',
      border: '#fed7aa',
      chipMuted: '#ffe4c7',
      hover: '#ffeada',
      today: '#ffe4c4',
      shadow: 'rgba(156, 59, 15, 0.12)',
      shadowStrong: 'rgba(156, 59, 15, 0.4)',
      nowLine: '#f97316',
    },
  },
  {
    id: 'forest',
    label: 'Orman',
    description: 'Toprak tonları, sakin',
    tokens: {
      bg: '#f1f5e9',
      bgPanel: '#ffffff',
      bgMuted: '#e3eddc',
      bgStrong: '#1b4332',
      text: '#1b4332',
      textMuted: '#2d6a4f',
      accentFrom: '#4ade80',
      accentTo: '#22c55e',
      border: '#c9e4c5',
      chipMuted: '#d6f1d0',
      hover: '#dff4d9',
      today: '#ccecc8',
      shadow: 'rgba(27, 67, 50, 0.08)',
      shadowStrong: 'rgba(27, 67, 50, 0.3)',
      nowLine: '#22c55e',
    },
  },
];

const eventTemplates: EventTemplate[] = [
  {
    id: 'team-meeting',
    name: 'Team meeting',
    title: 'Team sync',
    description: 'Quarterly roadmap, blockers, risks.',
    durationMinutes: 60,
    color: '#5f6af3',
    attendees: [
      { name: 'Product', status: 'accepted' },
      { name: 'Engineering', status: 'accepted' },
      { name: 'Design', status: 'maybe' },
    ],
    location: 'HQ · Room A',
  },
  {
    id: 'focus',
    name: 'Focus block',
    title: 'Deep focus',
    description: 'Heads-down work — no meetings.',
    durationMinutes: 90,
    color: '#0ea5e9',
    attendees: [],
    location: 'Desk / remote',
  },
  {
    id: '1-1',
    name: '1:1',
    title: '1:1',
    description: 'Weekly catch-up, growth, feedback.',
    durationMinutes: 45,
    color: '#1f9f83',
    attendees: [{ name: 'Manager', status: 'accepted' }],
    location: 'Cafe corner',
  },
  {
    id: 'demo',
    name: 'Demo',
    title: 'Product demo',
    description: 'Show progress, gather feedback, Q&A.',
    durationMinutes: 30,
    color: '#f2962f',
    attendees: [{ name: 'Stakeholders', status: 'maybe' }],
    onlineLink: 'https://meet.example.com/demo',
  },
];

const parseHour = (value: string, fallback: number) => {
  const hour = parseInt(value.split(':')[0] || value, 10);
  if (isNaN(hour)) return fallback;
  return Math.min(23, Math.max(0, hour));
};

const formatICSDate = (iso: string, allDay: boolean) => {
  const date = new Date(iso);
  const yyyy = date.getFullYear();
  const mm = pad(date.getMonth() + 1);
  const dd = pad(date.getDate());
  if (allDay) return `${yyyy}${mm}${dd}`;
  const hh = pad(date.getUTCHours());
  const min = pad(date.getUTCMinutes());
  return `${yyyy}${mm}${dd}T${hh}${min}00Z`;
};

const escapeICS = (value?: string) =>
  (value || '').replace(/\\n/g, '\\n').replace(/,/g, '\\,').replace(/;/g, '\\;');

const parseICSDate = (value: string) => {
  if (value.includes('T')) {
    const [datePart, timePart] = value.split('T');
    const iso = `${datePart.substring(0, 4)}-${datePart.substring(4, 6)}-${datePart.substring(
      6,
      8
    )}T${timePart.substring(0, 2)}:${timePart.substring(2, 4)}:00${value.endsWith('Z') ? 'Z' : ''}`;
    return new Date(iso);
  }
  const iso = `${value.substring(0, 4)}-${value.substring(4, 6)}-${value.substring(6, 8)}T00:00:00`;
  return new Date(iso);
};

const parseICS = (text: string): CalendarEvent[] => {
  const lines = text.split(/\r?\n/);
  const imported: CalendarEvent[] = [];
  let current:
    | (Partial<CalendarEvent> & {
        dtStartRaw?: string;
        dtEndRaw?: string;
        attendeesRaw?: { name: string; status: RSVPStatus }[];
      })
    | null = null;

  for (const raw of lines) {
    const line = raw.trim();
    if (line === 'BEGIN:VEVENT') {
      current = {};
      continue;
    }
    if (line === 'END:VEVENT' && current) {
      const start = current.dtStartRaw ? parseICSDate(current.dtStartRaw) : new Date();
      const end = current.dtEndRaw ? parseICSDate(current.dtEndRaw) : start;
      const allDay = !(current.dtStartRaw?.includes('T') || current.dtEndRaw?.includes('T'));
      const attendeesList =
        current.attendeesRaw?.map((item, index) => ({
          id: `ics-${index}-${crypto.randomUUID()}`,
          name: item.name,
          status: item.status,
        })) ?? [];
      imported.push({
        id: crypto.randomUUID(),
        title: current.title || 'Imported event',
        description: current.description,
        start: start.toISOString(),
        end: end.toISOString(),
        allDay,
        color: colorPalette[imported.length % colorPalette.length],
        recurrence: 'none',
        recurrenceEnd: null,
      exceptions: [],
      location: current.location,
      attendees: attendeesList,
      onlineLink: current.onlineLink,
      checklist: [],
      meetingNotes: '',
      calendarId: 'personal',
    });
      current = null;
      continue;
    }
    if (!current) continue;
    const [key, ...rest] = line.split(':');
    const value = rest.join(':');
    if (key.startsWith('SUMMARY')) current.title = value;
    if (key.startsWith('DESCRIPTION')) current.description = value.replace(/\\n/g, '\n');
    if (key.startsWith('DTSTART')) current.dtStartRaw = value;
    if (key.startsWith('DTEND')) current.dtEndRaw = value;
    if (key.startsWith('LOCATION')) current.location = value;
    if (key.startsWith('URL')) current.onlineLink = value;
    if (key.startsWith('ATTENDEE')) {
      const attendeeName = value.replace(/^mailto:/i, '');
      const statusMatch = key.match(/PARTSTAT=([^;:]*)/i);
      const rawStatus = statusMatch?.[1]?.toLowerCase();
      const status: RSVPStatus =
        rawStatus === 'declined' || rawStatus === 'tentative' || rawStatus === 'maybe'
          ? rawStatus === 'declined'
            ? 'declined'
            : 'maybe'
          : 'accepted';
      current.attendeesRaw = [...(current.attendeesRaw ?? []), { name: attendeeName, status }];
    }
  }

  return imported;
};

const encodeShareToken = (event: CalendarEvent | EventWithSpan) => {
  const payload = {
    ...event,
    sharedAt: nowIso(),
  };
  const json = JSON.stringify(payload);
  return encodeURIComponent(btoa(encodeURIComponent(json)));
};

const decodeShareToken = (token: string): CalendarEvent | null => {
  try {
    const decoded = decodeURIComponent(atob(token));
    const parsed = JSON.parse(decoded);
    const fallbackCalendar: CalendarId = calendars.some((cal) => cal.id === parsed.calendarId)
      ? parsed.calendarId
      : 'personal';
    const attendeesList: Attendee[] = Array.isArray(parsed.attendees)
      ? parsed.attendees.map((item: Attendee, index: number) => ({
          id: item.id || `share-${index}-${crypto.randomUUID()}`,
          name: item.name,
          status: item.status ?? 'accepted',
        }))
      : parseAttendees(parsed.attendees).map((name, index) => ({
          id: `share-${index}-${crypto.randomUUID()}`,
          name,
          status: 'accepted',
        }));
    const attachmentsList = normalizeAttachments(parsed.attachments);
    const checklistList = Array.isArray(parsed.checklist)
      ? parsed.checklist.map((item: ChecklistItem, index: number) => ({
          id: item.id || `cl-${index}-${crypto.randomUUID()}`,
          text: item.text,
          done: Boolean(item.done),
        }))
      : [];
    if (!parsed?.start || !parsed?.end) return null;
    return {
      id: parsed.id || crypto.randomUUID(),
      title: parsed.title || 'Shared event',
      description: parsed.description || '',
      start: parsed.start,
      end: parsed.end,
      allDay: Boolean(parsed.allDay),
      color: parsed.color || '#5f6af3',
      recurrence: parsed.recurrence ?? 'none',
      recurrenceEnd: parsed.recurrenceEnd ?? null,
      exceptions: parsed.exceptions ?? [],
      location: parsed.location,
      attendees: attendeesList,
      attachments: attachmentsList,
      checklist: checklistList,
      meetingNotes: parsed.meetingNotes || '',
      onlineLink: parsed.onlineLink,
      calendarId: fallbackCalendar,
    };
  } catch {
    return null;
  }
};

type VirtualListConfig = {
  rowHeight?: number;
  overscan?: number;
};

const useVirtualList = <T,>(
  items: T[],
  { rowHeight = 120, overscan = 4 }: VirtualListConfig = {}
) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [range, setRange] = useState({ start: 0, end: 0 });

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const calculate = () => {
      const height = container.clientHeight || 0;
      const scrollTop = container.scrollTop || 0;
      const start = Math.max(0, Math.floor(scrollTop / rowHeight) - overscan);
      const end = Math.min(
        items.length,
        Math.ceil((scrollTop + height) / rowHeight) + overscan
      );
      setRange((prev) => (prev.start === start && prev.end === end ? prev : { start, end }));
    };

    calculate();
    const onScroll = () => calculate();
    container.addEventListener('scroll', onScroll);
    const resizeObserver =
      typeof ResizeObserver !== 'undefined'
        ? new ResizeObserver(() => calculate())
        : null;
    resizeObserver?.observe(container);

    return () => {
      container.removeEventListener('scroll', onScroll);
      resizeObserver?.disconnect();
    };
  }, [items.length, overscan, rowHeight]);

  useEffect(() => {
    setRange((prev) => ({
      start: 0,
      end: Math.min(items.length, Math.max(prev.end, overscan * 2 + 1)),
    }));
  }, [items.length, overscan]);

  const visibleItems = useMemo(() => items.slice(range.start, range.end), [items, range.end, range.start]);
  const paddingTop = range.start * rowHeight;
  const renderedHeight = visibleItems.length * rowHeight;
  const totalHeight = items.length * rowHeight;
  const paddingBottom = Math.max(0, totalHeight - paddingTop - renderedHeight);

  return { containerRef, visibleItems, paddingTop, paddingBottom, totalHeight };
};

const getOccurrenceForDay = (event: CalendarEvent, day: Date): EventWithSpan | null => {
  const baseStartTime = new Date(event.start);
  const baseStartDay = startOfDay(baseStartTime);
  const targetDayStart = startOfDay(day);
  const targetDayEnd = addDays(targetDayStart, 1);
  const dayMs = 24 * 60 * 60 * 1000;
  const baseEnd = new Date(event.end);
  const durationMs = Math.max(baseEnd.getTime() - baseStartTime.getTime(), 0);
  const totalDays = Math.max(1, Math.ceil((durationMs + 1) / dayMs));

  const isAfterRecurrenceEnd = (anchorDay: Date) => {
    if (!event.recurrenceEnd) return false;
    const limit = startOfDay(new Date(event.recurrenceEnd));
    return anchorDay > limit;
  };

  const buildFromAnchor = (anchorStartTime: Date) => {
    const anchorStartDay = startOfDay(anchorStartTime);
    if (isAfterRecurrenceEnd(anchorStartDay)) return null;
    const anchorKey = toInputDate(anchorStartDay);
    if (event.exceptions?.includes(anchorKey)) return null;

    const anchorEnd = new Date(anchorStartTime.getTime() + durationMs);
    if (targetDayEnd <= anchorStartTime || targetDayStart > anchorEnd) return null;

    const spanIndex =
      Math.floor((startOfDay(targetDayStart).getTime() - startOfDay(anchorStartTime).getTime()) / dayMs) +
      1;

    const occurrenceStart = event.allDay
      ? targetDayStart
      : new Date(Math.max(anchorStartTime.getTime(), targetDayStart.getTime()));
    const occurrenceEnd = event.allDay
      ? new Date(Math.min(anchorEnd.getTime(), targetDayEnd.getTime() - 1000))
      : new Date(Math.min(anchorEnd.getTime(), targetDayEnd.getTime() - 1000));

    return {
      ...event,
      start: occurrenceStart.toISOString(),
      end: occurrenceEnd.toISOString(),
      spanIndex,
      spanTotal: totalDays,
      occurrenceKey: anchorKey,
    };
  };

  if (event.recurrence === 'none') {
    if (targetDayEnd <= baseStartTime) return null;
    return buildFromAnchor(baseStartTime);
  }

  if (targetDayEnd <= baseStartTime) return null;

  const diffDays = Math.floor((targetDayStart.getTime() - baseStartDay.getTime()) / dayMs);

  if (event.recurrence === 'daily' || event.recurrence === 'weekly') {
    const period = event.recurrence === 'daily' ? 1 : 7;
    const step = Math.floor(diffDays / period);
    const anchor = addDays(baseStartTime, step * period);
    return buildFromAnchor(anchor);
  }

  if (event.recurrence === 'monthly') {
    let anchor = new Date(baseStartTime);
    for (let i = 0; i < 120; i++) {
      if (anchor > targetDayStart) break;
      const occurrence = buildFromAnchor(anchor);
      if (occurrence) return occurrence;
      anchor = new Date(anchor);
      anchor.setMonth(anchor.getMonth() + 1);
    }
  }

  return null;
};

const App: React.FC = () => {
  const [view, setView] = useState<CalendarView>('month');
  const [currentDate, setCurrentDate] = useState(new Date());
  const [themeId, setThemeId] = useState<ThemeId>('light');
  const [customTheme, setCustomTheme] = useState<ThemeTokens>(themePresets[0].tokens);
  const [events, setEvents] = useState<CalendarEvent[]>(seededEvents);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<EventForm>(defaultForm(new Date()));
  const [dragPayload, setDragPayload] = useState<{ id: string; start: string } | null>(null);
  const [resizeState, setResizeState] = useState<{
    id: string;
    edge: 'start' | 'end';
    originY: number;
    originalStart: string;
    originalEnd: string;
  } | null>(null);
  const [hoveredDay, setHoveredDay] = useState<string | null>(null);
  const [lastAction, setLastAction] = useState<{ events: CalendarEvent[]; label: string } | null>(
    null
  );
  const [filters, setFilters] = useState<Filters>({
    query: '',
    color: '',
    from: '',
    to: '',
    name: '',
    calendars: calendars.map((c) => c.id),
    location: '',
    attendee: '',
    onlineLink: '',
    heatmap: true,
  });
  const [savedFilters, setSavedFilters] = useState<Filters[]>([]);
  const [reminders, setReminders] = useState<Reminder[]>([]);
  const [activityLog, setActivityLog] = useState<ActivityLog[]>([]);
  const [formErrors, setFormErrors] = useState<FormErrors>({});
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [isShortcutsOpen, setIsShortcutsOpen] = useState(false);
  const [summaryRange, setSummaryRange] = useState<'week' | 'month'>('week');
  const [timezone, setTimezone] = useState(
    Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'
  );
  const [workHours, setWorkHours] = useState<{ start: number; end: number }>({ start: 9, end: 18 });
  const [shareToast, setShareToast] = useState<string | null>(null);
  const [sharedViewEvent, setSharedViewEvent] = useState<CalendarEvent | null>(null);
  const [isPublicViewOpen, setIsPublicViewOpen] = useState(false);
  const [lastTemplateId, setLastTemplateId] = useState<string | null>(null);
  const [conflictWarning, setConflictWarning] = useState<string | null>(null);
  const [nowLineTop, setNowLineTop] = useState<number | null>(null);
  const timelineGridRef = useRef<HTMLDivElement | null>(null);
  const [activeTheme, setActiveTheme] = useState<ThemeTokens>(themePresets[0].tokens);
  const [recentAttendees, setRecentAttendees] = useState<string[]>([]);

  const monthDays = useMemo(() => getMonthGrid(currentDate), [currentDate]);
  const weekDays = useMemo(() => {
    const start = startOfWeek(currentDate);
    return Array.from({ length: 7 }, (_, index) => addDays(start, index));
  }, [currentDate]);
  const timelineHours = useMemo(() => {
    const start = Math.min(workHours.start, workHours.end - 1);
    const end = Math.max(workHours.end, workHours.start + 1);
    const hours: number[] = [];
    for (let h = start; h < end; h++) {
      hours.push(h);
    }
    return hours;
  }, [workHours]);

  const colorOptions = useMemo(
    () => Array.from(new Set(events.map((e) => e.color))).sort(),
    [events]
  );

  const addActivity = useCallback((message: string, eventId?: string) => {
    const entry: ActivityLog = { id: crypto.randomUUID(), at: nowIso(), message, eventId };
    setActivityLog((prev) => [entry, ...prev].slice(0, 100));
  }, []);

  const addRecentAttendee = useCallback((name: string) => {
    const clean = name.trim();
    if (!clean) return;
    setRecentAttendees((prev) => {
      const exists = prev.find((item) => item.toLowerCase() === clean.toLowerCase());
      if (exists) return prev;
      return [clean, ...prev].slice(0, 12);
    });
  }, []);

  useEffect(() => {
    const names = Array.from(
      new Set(
        events
          .flatMap((e) => e.attendees ?? [])
          .map((a) => a.name.trim())
          .filter(Boolean)
      )
    );
    setRecentAttendees((prev) => {
      const merged = [...prev];
      names.forEach((name) => {
        const exists = merged.find((item) => item.toLowerCase() === name.toLowerCase());
        if (!exists) merged.push(name);
      });
      return merged.slice(0, 20);
    });
  }, [events]);

  const toggleChecklistItem = useCallback((eventId: string, itemId: string) => {
    setEvents((prev) =>
      prev.map((event) => {
        if (event.id !== eventId) return event;
        const checklist = event.checklist?.map((item) =>
          item.id === itemId ? { ...item, done: !item.done } : item
        );
        return { ...event, checklist };
      })
    );
  }, []);

  const findConflicts = useCallback(
    (candidate: CalendarEvent) => {
      const start = new Date(candidate.start).getTime();
      const end = new Date(candidate.end).getTime();
      return events.filter((other) => {
        if (other.id === candidate.id) return false;
        const otherStart = new Date(other.start).getTime();
        const otherEnd = new Date(other.end).getTime();
        return start < otherEnd && end > otherStart;
      });
    },
    [events]
  );

  const updateCustomTheme = useCallback((key: keyof ThemeTokens, value: string) => {
    setCustomTheme((prev) => ({ ...prev, [key]: value }));
    setThemeId('custom');
  }, []);

  const applyTheme = useCallback(
    (tokens: ThemeTokens) => {
      const root = document.documentElement;
      root.style.setProperty('--bg', tokens.bg);
      root.style.setProperty('--bg-panel', tokens.bgPanel);
      root.style.setProperty('--bg-muted', tokens.bgMuted);
      root.style.setProperty('--bg-strong', tokens.bgStrong);
      root.style.setProperty('--text', tokens.text);
      root.style.setProperty('--text-muted', tokens.textMuted);
      root.style.setProperty('--accent', `linear-gradient(135deg, ${tokens.accentFrom}, ${tokens.accentTo})`);
      root.style.setProperty('--border', tokens.border);
      root.style.setProperty('--chip-muted', tokens.chipMuted);
      root.style.setProperty('--hover', tokens.hover);
      root.style.setProperty('--today', tokens.today);
      root.style.setProperty('--shadow', tokens.shadow);
      root.style.setProperty('--shadow-strong', tokens.shadowStrong);
      root.style.setProperty('--now-line', tokens.nowLine);
      setActiveTheme(tokens);
    },
    []
  );

  const timezoneOptions = useMemo(() => {
    const base = [
      Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
      'UTC',
      'Europe/Istanbul',
      'Europe/London',
      'America/New_York',
      'Asia/Tokyo',
    ];
    return Array.from(new Set(base));
  }, []);

  const eventsForDay = useCallback(
    (day: Date) =>
      events
        .map((event) => getOccurrenceForDay(event, day))
        .filter((item): item is EventWithSpan => Boolean(item))
        .filter((item) => matchesFilters(item, day, filters))
        .sort(
          (a, b) =>
            new Date(a.start).getTime() - new Date(b.start).getTime() ||
            a.title.localeCompare(b.title)
        ),
    [events, filters]
  );

  const attendeeSuggestions = useMemo(() => {
    const pool = Array.from(new Set(recentAttendees));
    const existing = new Set(form.attendees.map((a) => a.name.toLowerCase()));
    const query = form.newAttendeeName.trim().toLowerCase();
    return pool
      .filter((name) => !existing.has(name.toLowerCase()))
      .filter((name) => (query ? name.toLowerCase().includes(query) : true))
      .slice(0, 8);
  }, [form.attendees, form.newAttendeeName, recentAttendees]);

  const activeFilterCount = useMemo(() => {
    const keys: (keyof Filters)[] = ['query', 'color', 'from', 'to', 'location', 'attendee', 'onlineLink'];
    return keys.filter((key) => {
      const value = filters[key];
      if (Array.isArray(value)) return value.length && value.length !== calendars.length;
      return Boolean(value && (value as string).length);
    }).length;
  }, [calendars.length, filters]);

  const formatTimeTz = useCallback((date: Date) => formatTime(date, timezone), [timezone]);

  const currentFilteredCount = useMemo(() => {
    const day = currentDate;
    return eventsForDay(day).length;
  }, [currentDate, eventsForDay]);

  const summaryWindow = useMemo(() => {
    const start =
      summaryRange === 'week'
        ? startOfWeek(currentDate)
        : new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
    const end =
      summaryRange === 'week'
        ? addDays(start, 7)
        : new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1);
    return { start, end };
  }, [currentDate, summaryRange]);

  const monthHeat = useMemo(() => {
    const map: Record<string, number> = {};
    let max = 0;
    monthDays.forEach((day) => {
      const count = eventsForDay(day).length;
      map[toInputDate(day)] = count;
      if (count > max) max = count;
    });
    return { map, max: Math.max(max, 1) };
  }, [eventsForDay, monthDays]);

  const moveEventToDay = (eventId: string, targetDay: Date) => {
    const targetEvent = events.find((e) => e.id === eventId);
    if (!targetEvent) return;
    if (targetEvent.recurrence !== 'none') {
      const confirmed = window.confirm('Apply move to the whole recurring series?');
      if (!confirmed) return;
    }
    const duration = new Date(targetEvent.end).getTime() - new Date(targetEvent.start).getTime();
    const baseStart = new Date(targetEvent.start);
    const updatedStart = targetEvent.allDay
      ? new Date(targetDay.getFullYear(), targetDay.getMonth(), targetDay.getDate(), 0, 0, 0)
      : new Date(
          targetDay.getFullYear(),
          targetDay.getMonth(),
          targetDay.getDate(),
          baseStart.getHours(),
          baseStart.getMinutes(),
          baseStart.getSeconds()
        );
    const updatedEnd = targetEvent.allDay
      ? new Date(targetDay.getFullYear(), targetDay.getMonth(), targetDay.getDate(), 23, 59, 59)
      : new Date(updatedStart.getTime() + duration);
    const candidate: CalendarEvent = { ...targetEvent, start: updatedStart.toISOString(), end: updatedEnd.toISOString() };
    const conflicts = findConflicts(candidate);
    if (conflicts.length) {
      const label = conflicts
        .slice(0, 3)
        .map((c) => c.title)
        .join(', ');
      setConflictWarning(`Çakışan etkinlikler: ${label}${conflicts.length > 3 ? ` +${conflicts.length - 3}` : ''}`);
      const proceed = window.confirm(
        `Bu zaman aralığında ${conflicts.length} etkinlik var: ${label}. Taşımaya devam edilsin mi?`
      );
      if (!proceed) return;
    } else {
      setConflictWarning(null);
    }

    setLastAction({ events: cloneEvents(events), label: 'Moved event' });
    setEvents((prev) =>
      prev.map((item) => (item.id === eventId ? { ...item, start: candidate.start, end: candidate.end } : item))
    );
    addActivity(`Etkinlik taşındı: ${targetEvent.title} → ${toInputDate(targetDay)}`, targetEvent.id);
  };

  const skipOccurrence = (occurrence: EventWithSpan) => {
    const occurrenceDate = occurrence.occurrenceKey ?? toInputDate(new Date(occurrence.start));
    if (occurrence.recurrence !== 'none') {
      const confirmed = window.confirm('Skip only this date in the series?');
      if (!confirmed) return;
    }
    setLastAction({ events: cloneEvents(events), label: 'Skipped occurrence' });
    setEvents((prev) =>
      prev.map((item) => {
        if (item.id !== occurrence.id) return item;
        const existing = item.exceptions ?? [];
        if (existing.includes(occurrenceDate)) return item;
        return { ...item, exceptions: [...existing, occurrenceDate] };
      })
    );
    addActivity(`Tekrar atlandı: ${occurrence.title} (${occurrenceDate})`, occurrence.id);
  };

  const detachOccurrence = (occurrence: EventWithSpan) => {
    if (occurrence.recurrence === 'none') return;
    const confirmed = window.confirm('Detach only this occurrence from the series?');
    if (!confirmed) return;
    setLastAction({ events: cloneEvents(events), label: 'Detached occurrence' });
    const exceptionDate = occurrence.occurrenceKey ?? toInputDate(new Date(occurrence.start));
    const detached: CalendarEvent = {
      ...occurrence,
      id: crypto.randomUUID(),
      recurrence: 'none',
      recurrenceEnd: null,
      exceptions: [],
    };
    setEvents((prev) => [
      ...prev.map((item) => {
        if (item.id !== occurrence.id) return item;
        const existing = item.exceptions ?? [];
        if (existing.includes(exceptionDate)) return item;
        return { ...item, exceptions: [...existing, exceptionDate] };
      }),
      detached,
    ]);
    addActivity(`Seriden ayrıldı: ${occurrence.title} (${exceptionDate})`, detached.id);
  };

  const upcoming = useMemo(() => {
    const start = addDays(new Date(), -1);
    const horizonDays = 90;
    const collected: CalendarEvent[] = [];

    for (let i = 0; i < horizonDays && collected.length < 12; i++) {
      const day = addDays(start, i);
      collected.push(...eventsForDay(day));
    }

    return collected
      .filter((event) => new Date(event.start) >= start)
      .sort(
        (a, b) =>
          new Date(a.start).getTime() - new Date(b.start).getTime() ||
          a.title.localeCompare(b.title)
      )
      .slice(0, 6);
  }, [eventsForDay]);

  const summary = useMemo(() => {
    const { start, end } = summaryWindow;
    const inRange = events.filter((event) => {
      const eventStart = new Date(event.start);
      return eventStart >= start && eventStart < end;
    });

    const meetingCount = inRange.filter((e) => !e.allDay).length;
    const allDayCount = inRange.filter((e) => e.allDay).length;
    const focusCount = inRange.filter((e) => e.title.toLowerCase().includes('focus')).length;
    const totalMs = sumDurations(inRange.filter((e) => !e.allDay));
    const totalHours = Math.round((totalMs / (1000 * 60 * 60)) * 10) / 10;

    return { meetingCount, allDayCount, focusCount, totalHours };
  }, [events, summaryWindow]);

  const collectRangeEvents = useCallback(() => {
    const rows: { event: EventWithSpan; day: Date }[] = [];
    const { start, end } = summaryWindow;
    for (let cursor = new Date(start); cursor < end; cursor = addDays(cursor, 1)) {
      const daily = eventsForDay(cursor);
      rows.push(
        ...daily.map((event) => ({
          event,
          day: new Date(cursor),
        }))
      );
    }
    return rows.sort(
      (a, b) =>
        new Date(a.event.start).getTime() - new Date(b.event.start).getTime() ||
        a.event.title.localeCompare(b.event.title)
    );
  }, [eventsForDay, summaryWindow]);

  const exportRangeCSV = useCallback(() => {
    const rows = collectRangeEvents();
    if (rows.length === 0) {
      alert('Bu aralıkta etkinlik yok.');
      return;
    }

    const escapeCsv = (value: string) => `"${(value || '').replace(/"/g, '""')}"`;
    const header = [
      'Date',
      'Start',
      'End',
      'Title',
      'Calendar',
      'Location',
      'Attendees',
      'All day',
      'Online link',
      'Description',
    ];

    const csvRows = rows.map(({ event, day }) => {
      const startLabel = event.allDay ? 'All day' : formatTimeTz(new Date(event.start));
      const endLabel = event.allDay ? '' : formatTimeTz(new Date(event.end));
      const attendeeLabel =
        event.attendees
          ?.map((person) => `${person.name} (${rsvpLabel[person.status]})`)
          .join('; ') ?? '';
      const calendarLabel = calendars.find((c) => c.id === event.calendarId)?.label ?? event.calendarId;

      return [
        toInputDate(day),
        startLabel,
        endLabel,
        event.title,
        calendarLabel,
        event.location || '',
        attendeeLabel,
        event.allDay ? 'Yes' : 'No',
        event.onlineLink || '',
        event.description || '',
      ]
        .map(escapeCsv)
        .join(',');
    });

    const content = [header.join(','), ...csvRows].join('\n');
    const fileLabel =
      summaryRange === 'week'
        ? `week-${toInputDate(summaryWindow.start)}`
        : `month-${summaryWindow.start.getFullYear()}-${pad(summaryWindow.start.getMonth() + 1)}`;
    const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `lodos-report-${fileLabel}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }, [collectRangeEvents, formatTimeTz, summaryRange, summaryWindow]);

  const exportRangePDF = useCallback(() => {
    const rows = collectRangeEvents();
    if (rows.length === 0) {
      alert('Bu aralıkta etkinlik yok.');
      return;
    }

    const escapeHtml = (value: string) =>
      (value || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    const { start, end } = summaryWindow;
    const rangeLabel = `${formatDate(start)} - ${formatDate(addDays(end, -1))}`;
    const title = summaryRange === 'week' ? 'Haftalık rapor' : 'Aylık rapor';

    const tableRows = rows
      .map(({ event, day }) => {
        const startLabel = event.allDay ? 'All day' : formatTimeTz(new Date(event.start));
        const endLabel = event.allDay ? '' : formatTimeTz(new Date(event.end));
        const calendarLabel = calendars.find((c) => c.id === event.calendarId)?.label ?? event.calendarId;
        const attendeeLabel =
          event.attendees
            ?.map((person) => `${person.name} (${rsvpLabel[person.status]})`)
            .join(', ') ?? '';
        return `<tr>
            <td>${toInputDate(day)}</td>
            <td>${escapeHtml(startLabel)}</td>
            <td>${escapeHtml(endLabel)}</td>
            <td>${escapeHtml(event.title)}</td>
            <td>${escapeHtml(calendarLabel)}</td>
            <td>${escapeHtml(event.location || '')}</td>
            <td>${escapeHtml(attendeeLabel)}</td>
          </tr>`;
      })
      .join('');

    const popup = window.open('', '_blank');
    if (!popup) return;
    popup.document.write(`
      <html>
        <head>
          <title>${title}</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 24px; color: #0f172a; }
            h1 { margin: 0 0 8px; font-size: 22px; }
            .range { margin: 0 0 16px; color: #475569; font-size: 14px; }
            table { width: 100%; border-collapse: collapse; font-size: 12px; }
            th, td { border: 1px solid #cbd5e1; padding: 8px; text-align: left; }
            th { background: #e2e8f0; }
            tr:nth-child(even) { background: #f8fafc; }
          </style>
        </head>
        <body>
          <h1>${title}</h1>
          <p class="range">${rangeLabel} · ${timezone}</p>
          <table>
            <thead>
              <tr>
                <th>Tarih</th>
                <th>Başlangıç</th>
                <th>Bitiş</th>
                <th>Başlık</th>
                <th>Takvim</th>
                <th>Konum</th>
                <th>Katılımcılar</th>
              </tr>
            </thead>
            <tbody>
              ${tableRows}
            </tbody>
          </table>
        </body>
      </html>
    `);
    popup.document.close();
    popup.focus();
    setTimeout(() => popup.print(), 200);
  }, [collectRangeEvents, formatTimeTz, summaryRange, summaryWindow, timezone]);

  const beginDrag = (calendarEvent: CalendarEvent) => (e: React.DragEvent) => {
    setDragPayload({ id: calendarEvent.id, start: calendarEvent.start });
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData(
      'text/plain',
      JSON.stringify({ id: calendarEvent.id, start: calendarEvent.start })
    );
  };

  const endDrag = () => setDragPayload(null);

  const handleDropOnDay = (day: Date) => (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!dragPayload) return;
    moveEventToDay(dragPayload.id, day);
    setDragPayload(null);
    setHoveredDay(null);
  };

  const handleDragOver = (e: React.DragEvent) => {
    if (dragPayload) {
      e.preventDefault();
      e.dataTransfer.dropEffect = 'move';
    }
  };

  const handleDragEnterDay = (day: Date) => (e: React.DragEvent) => {
    if (!dragPayload) return;
    e.preventDefault();
    setHoveredDay(day.toISOString());
  };

  const handleDragLeaveDay = (day: Date) => (e: React.DragEvent) => {
    if (hoveredDay === day.toISOString()) {
      setHoveredDay(null);
    }
  };

  const undoLastChange = () => {
    if (!lastAction) return;
    setEvents(lastAction.events);
    setLastAction(null);
  };

  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }
  }, []);

  useEffect(() => {
    const preset = themePresets.find((t) => t.id === themeId);
    const tokens = themeId === 'custom' ? customTheme : preset?.tokens ?? themePresets[0].tokens;
    applyTheme(tokens);
  }, [applyTheme, customTheme, themeId]);

  useEffect(() => {
    const interval = setInterval(() => {
      const now = nowIso();
      setReminders((prev) =>
        prev.map((reminder) => {
          if (reminder.dismissed) return reminder;
          if (reminder.at <= now) {
            if (!('Notification' in window) || Notification.permission !== 'granted') {
              alert(reminder.message);
              return { ...reminder, dismissed: true };
            } else {
              new Notification(reminder.message);
              return { ...reminder, dismissed: true };
            }
          }
          return reminder;
        })
      );
    }, 10000);

    return () => clearInterval(interval);
  }, []);

  const pendingReminders = reminders
    .filter((r) => !r.dismissed && r.at > nowIso())
    .sort((a, b) => new Date(a.at).getTime() - new Date(b.at).getTime())
    .slice(0, 5);

  const exportICS = () => {
    const lines = ['BEGIN:VCALENDAR', 'VERSION:2.0', 'PRODID:-//Lodos Calendar//EN'];
    events.forEach((event) => {
      lines.push('BEGIN:VEVENT');
      lines.push(`UID:${event.id}`);
      lines.push(`SUMMARY:${escapeICS(event.title)}`);
      if (event.description) lines.push(`DESCRIPTION:${escapeICS(event.description)}`);
      if (event.location) lines.push(`LOCATION:${escapeICS(event.location)}`);
      if (event.onlineLink) lines.push(`URL:${escapeICS(event.onlineLink)}`);
      lines.push(`CATEGORIES:${event.calendarId}`);
      if (event.attendees?.length) {
        event.attendees.forEach((attendee) => {
          lines.push(`ATTENDEE;PARTSTAT=${rsvpICS[attendee.status]}:${escapeICS(attendee.name)}`);
        });
      }
      lines.push(`DTSTART:${formatICSDate(event.start, event.allDay)}`);
      lines.push(`DTEND:${formatICSDate(event.end, event.allDay)}`);
      lines.push('END:VEVENT');
    });
    lines.push('END:VCALENDAR');

    const blob = new Blob([lines.join('\r\n')], { type: 'text/calendar' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'lodos-calendar.ics';
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleImportICS = (file: File) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target?.result?.toString() ?? '';
      const imported = parseICS(text);
      if (imported.length === 0) return;
      setLastAction({ events: cloneEvents(events), label: 'Imported .ics' });
      setEvents((prev) => [...prev, ...imported]);
      addActivity(`${imported.length} etkinlik içe aktarıldı (.ics)`);
    };
    reader.readAsText(file);
  };

  const startResize =
    (calendarEvent: CalendarEvent, edge: 'start' | 'end') => (e: React.MouseEvent) => {
      if (calendarEvent.allDay) return;
      if (calendarEvent.recurrence !== 'none') {
        const confirmed = window.confirm('Resize applies to the entire series. Continue?');
        if (!confirmed) return;
      }
      e.stopPropagation();
      e.preventDefault();
      setLastAction({ events: cloneEvents(events), label: 'Resized event' });
      setResizeState({
        id: calendarEvent.id,
        edge,
        originY: e.clientY,
        originalStart: calendarEvent.start,
        originalEnd: calendarEvent.end,
      });
    };

  useEffect(() => {
    if (!resizeState) return;

    const pixelsPerStep = 8; // lower is more sensitive
    const stepMinutes = 15;

    const onMove = (e: MouseEvent) => {
      const deltaSteps = Math.round((e.clientY - resizeState.originY) / pixelsPerStep);
      const deltaMinutes = deltaSteps * stepMinutes;
      if (deltaMinutes === 0) return;

      setEvents((prev) =>
        prev.map((item) => {
          if (item.id !== resizeState.id) return item;
          if (item.allDay) return item;

          const originalStart = new Date(resizeState.originalStart);
          const originalEnd = new Date(resizeState.originalEnd);

          let nextStart = new Date(originalStart);
          let nextEnd = new Date(originalEnd);

          if (resizeState.edge === 'start') {
            nextStart = new Date(originalStart.getTime() + deltaMinutes * 60000);
            if (nextStart >= new Date(originalEnd.getTime() - 15 * 60000)) {
              nextStart = new Date(originalEnd.getTime() - 15 * 60000);
            }
          } else {
            nextEnd = new Date(originalEnd.getTime() + deltaMinutes * 60000);
            if (nextEnd <= new Date(originalStart.getTime() + 15 * 60000)) {
              nextEnd = new Date(originalStart.getTime() + 15 * 60000);
            }
          }

          const dayStart = startOfDay(originalStart);
          const dayEnd = addDays(dayStart, 1);
          if (nextStart < dayStart) nextStart = dayStart;
          if (nextEnd > dayEnd) nextEnd = dayEnd;

          return { ...item, start: nextStart.toISOString(), end: nextEnd.toISOString() };
        })
      );
    };

    const onUp = () => setResizeState(null);

    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [resizeState]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const shareParam = params.get('share');
    if (!shareParam) return;
    const decoded = decodeShareToken(shareParam);
    if (decoded) {
      setSharedViewEvent(decoded);
      setIsPublicViewOpen(true);
    }
  }, []);

  useEffect(() => {
    if (!shareToast) return;
    const timer = setTimeout(() => setShareToast(null), 2500);
    return () => clearTimeout(timer);
  }, [shareToast]);

  const handlePrev = useCallback(() => {
    const updated = new Date(currentDate);
    if (view === 'month') {
      updated.setMonth(updated.getMonth() - 1);
    } else if (view === 'week' || view === 'timeline') {
      updated.setDate(updated.getDate() - 7);
    } else {
      updated.setDate(updated.getDate() - 1);
    }
    setCurrentDate(updated);
  }, [currentDate, view]);

  const handleNext = useCallback(() => {
    const updated = new Date(currentDate);
    if (view === 'month') {
      updated.setMonth(updated.getMonth() + 1);
    } else if (view === 'week' || view === 'timeline') {
      updated.setDate(updated.getDate() + 7);
    } else {
      updated.setDate(updated.getDate() + 1);
    }
    setCurrentDate(updated);
  }, [currentDate, view]);

  const openNewEvent = useCallback((anchor?: Date) => {
    const startHour = Math.min(Math.max(workHours.start, 0), 23);
    const endHour = Math.min(Math.max(workHours.end, startHour + 1), 24);
    const base = defaultForm(anchor ?? currentDate);
    setForm({
      ...base,
      startTime: `${pad(startHour)}:00`,
      endTime: `${pad(Math.max(startHour + 1, endHour))}:00`,
      calendarId: form.calendarId,
    });
    setEditingId(null);
    setIsModalOpen(true);
  }, [currentDate, form.calendarId, workHours.end, workHours.start]);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      switch (e.key.toLowerCase()) {
        case 't': {
          setCurrentDate(new Date());
          break;
        }
        case 'n': {
          openNewEvent(currentDate);
          break;
        }
        case 'arrowleft': {
          handlePrev();
          break;
        }
        case 'arrowright': {
          handleNext();
          break;
        }
        case '1': {
          setView('day');
          break;
        }
        case '2': {
          setView('week');
          break;
        }
        case '3': {
          setView('month');
          break;
        }
        case '4': {
          setView('timeline');
          break;
        }
        default:
          break;
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [currentDate, handleNext, handlePrev, openNewEvent, setCurrentDate, setView]);

  const openForEdit = (event: CalendarEvent) => {
    const startDate = new Date(event.start);
    const endDate = new Date(event.end);
    setForm({
      title: event.title,
      date: toInputDate(startDate),
      startTime: pad(startDate.getHours()) + ':' + pad(startDate.getMinutes()),
      endTime: pad(endDate.getHours()) + ':' + pad(endDate.getMinutes()),
      description: event.description ?? '',
      allDay: event.allDay,
      color: event.color,
      repeat: event.recurrence,
      repeatUntil: event.recurrenceEnd ? toInputDate(new Date(event.recurrenceEnd)) : '',
      location: event.location ?? '',
      attendees: event.attendees ?? [],
      newAttendeeName: '',
      newAttendeeStatus: 'accepted',
      attachments: normalizeAttachments(event.attachments),
      newAttachmentLabel: '',
      newAttachmentUrl: '',
      onlineLink: event.onlineLink ?? '',
      meetingNotes: event.meetingNotes ?? '',
      checklist: event.checklist ?? [],
      newChecklistText: '',
      calendarId: event.calendarId,
    });
    setEditingId(event.id);
    setIsModalOpen(true);
  };

  const addAttendeeToForm = () => {
    const name = form.newAttendeeName.trim();
    if (!name) return;
    setForm((prev) => ({
      ...prev,
      attendees: [
        ...prev.attendees,
        { id: crypto.randomUUID(), name, status: prev.newAttendeeStatus },
      ],
      newAttendeeName: '',
      newAttendeeStatus: 'accepted',
    }));
    addRecentAttendee(name);
  };

  const updateAttendeeStatusInForm = (id: string, status: RSVPStatus) => {
    setForm((prev) => ({
      ...prev,
      attendees: prev.attendees.map((attendee) =>
        attendee.id === id ? { ...attendee, status } : attendee
      ),
    }));
  };

  const removeAttendeeFromForm = (id: string) => {
    setForm((prev) => ({
      ...prev,
      attendees: prev.attendees.filter((attendee) => attendee.id !== id),
    }));
  };

  const addAttachmentToForm = () => {
    const label = form.newAttachmentLabel.trim() || form.newAttachmentUrl.trim();
    const url = form.newAttachmentUrl.trim();
    if (!label || !url) return;
    setForm((prev) => ({
      ...prev,
      attachments: [...prev.attachments, { id: crypto.randomUUID(), label, url }],
      newAttachmentLabel: '',
      newAttachmentUrl: '',
    }));
  };

  const removeAttachmentFromForm = (id: string) => {
    setForm((prev) => ({
      ...prev,
      attachments: prev.attachments.filter((att) => att.id !== id),
    }));
  };

  const addChecklistItemToForm = () => {
    const text = form.newChecklistText.trim();
    if (!text) return;
    setForm((prev) => ({
      ...prev,
      checklist: [...prev.checklist, { id: crypto.randomUUID(), text, done: false }],
      newChecklistText: '',
    }));
  };

  const toggleChecklistItemInForm = (id: string) => {
    setForm((prev) => ({
      ...prev,
      checklist: prev.checklist.map((item) =>
        item.id === id ? { ...item, done: !item.done } : item
      ),
    }));
  };

  const removeChecklistItemFromForm = (id: string) => {
    setForm((prev) => ({
      ...prev,
      checklist: prev.checklist.filter((item) => item.id !== id),
    }));
  };

  const copyShareLink = (event: CalendarEvent | EventWithSpan) => {
    const token = encodeShareToken(event);
    const url = `${window.location.origin}${window.location.pathname}?share=${token}`;
    if (navigator?.clipboard?.writeText) {
      navigator.clipboard.writeText(url).then(
        () => setShareToast('Paylaşım linki kopyalandı'),
        () => setShareToast('Kopyalanamadı, linki manuel kopyalayın')
      );
    } else {
      window.prompt('Paylaşım linki', url);
    }
  };

  const openPublicPreview = (event: CalendarEvent | EventWithSpan) => {
    setSharedViewEvent(event);
    setIsPublicViewOpen(true);
  };

  const closePublicView = () => {
    setIsPublicViewOpen(false);
    setSharedViewEvent(null);
    const params = new URLSearchParams(window.location.search);
    if (params.has('share')) {
      params.delete('share');
      const next = params.toString();
      const nextUrl = next ? `${window.location.pathname}?${next}` : window.location.pathname;
      window.history.replaceState({}, '', nextUrl);
    }
  };

  const formatSharedRange = (event: CalendarEvent) => {
    const start = new Date(event.start);
    const end = new Date(event.end);
    if (event.allDay) return `${formatDate(start)} · Tüm gün`;
    const sameDay = isSameDay(start, end);
    if (sameDay) return `${formatDate(start)} · ${formatTimeTz(start)} – ${formatTimeTz(end)}`;
    return `${formatDate(start)} ${formatTimeTz(start)} → ${formatDate(end)} ${formatTimeTz(end)}`;
  };

  const applyTemplate = (template: EventTemplate) => {
    const attendeeList =
      template.attendees?.map((item, index) => ({
        id: `${template.id}-attendee-${index}-${crypto.randomUUID()}`,
        name: item.name,
        status: item.status ?? 'accepted',
      })) ?? [];
    const baseStart = form.startTime || '09:00';
    const templateEnd = template.durationMinutes
      ? addMinutesToTime(baseStart, template.durationMinutes)
      : form.endTime;
    setForm((prev) => ({
      ...prev,
      title: template.title,
      description: template.description,
      color: template.color ?? prev.color,
      location: template.location ?? prev.location,
      onlineLink: template.onlineLink ?? prev.onlineLink,
      repeat: template.recurrence ?? prev.repeat,
      allDay: template.allDay ?? prev.allDay,
      startTime: baseStart,
      endTime: templateEnd,
      attendees: attendeeList,
      newAttendeeName: '',
      newAttendeeStatus: 'accepted',
      attachments: prev.attachments,
      newAttachmentLabel: '',
      newAttachmentUrl: '',
    }));
    setLastTemplateId(template.id);
    setShareToast(`${template.name} şablonu uygulandı`);
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    const editingEvent = editingId ? events.find((item) => item.id === editingId) : null;
    if (editingEvent && editingEvent.recurrence !== 'none') {
      const confirmed = window.confirm('This will update the entire series. Continue?');
      if (!confirmed) return;
    }

    const errors: FormErrors = {};
    if (!form.title.trim()) errors.title = 'Başlık gerekli';
    if (!form.date) errors.date = 'Tarih gerekli';
    if (!isValidUrl(form.onlineLink)) errors.onlineLink = 'Geçerli bir URL girin (http/https)';

    let start: Date | null = null;
    let end: Date | null = null;

    if (form.allDay) {
      start = new Date(`${form.date}T00:00:00`);
      end = new Date(`${form.date}T23:59:59`);
    } else {
      if (!form.startTime) errors.startTime = 'Başlangıç saati gerekli';
      if (!form.endTime) errors.endTime = 'Bitiş saati gerekli';
      if (!errors.startTime && !errors.endTime && !errors.date) {
        start = new Date(`${form.date}T${form.startTime}`);
        end = new Date(`${form.date}T${form.endTime || form.startTime}`);
        if (isNaN(start.getTime()) || isNaN(end.getTime())) {
          errors.timeRange = 'Saat formatı geçersiz';
        } else if (end <= start) {
          errors.timeRange = 'Bitiş saati başlangıçtan sonra olmalı';
        }
      }
    }

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    setFormErrors({});
    const safeStart = start ?? new Date(`${form.date}T00:00:00`);
    const safeEnd = end ?? new Date(`${form.date}T23:59:59`);
    const normalizedAttachments = normalizeAttachments(form.attachments);

    const normalizedAttendees =
      form.attendees?.map((attendee, index) => ({
        id: attendee.id || `form-${index}-${crypto.randomUUID()}`,
        name: attendee.name.trim(),
        status: attendee.status,
      })) ?? [];

    const payload: CalendarEvent = {
      id: editingId ?? crypto.randomUUID(),
      title: form.title.trim(),
      description: form.description,
      start: safeStart.toISOString(),
      end: safeEnd.toISOString(),
      allDay: form.allDay,
      color: form.color,
      recurrence: form.repeat,
      recurrenceEnd: form.repeat === 'none' || !form.repeatUntil
        ? null
        : new Date(`${form.repeatUntil}T23:59:59`).toISOString(),
      exceptions: editingEvent?.exceptions ?? [],
      location: form.location,
      attendees: normalizedAttendees,
      attachments: normalizedAttachments,
      meetingNotes: form.meetingNotes,
      checklist: form.checklist,
      onlineLink: form.onlineLink,
      calendarId: form.calendarId,
    };

    const conflicts = findConflicts(payload);
    if (conflicts.length) {
      const label = conflicts
        .slice(0, 3)
        .map((c) => c.title)
        .join(', ');
      setConflictWarning(`Çakışan etkinlikler: ${label}${conflicts.length > 3 ? ` +${conflicts.length - 3}` : ''}`);
      const proceed = window.confirm(
        `Bu zaman aralığında ${conflicts.length} etkinlik var: ${label}. Kaydetmeye devam edilsin mi?`
      );
      if (!proceed) return;
    } else {
      setConflictWarning(null);
    }

    if (editingId) {
      setLastAction({ events: cloneEvents(events), label: 'Updated series' });
      setEvents((prev) => prev.map((item) => (item.id === editingId ? payload : item)));
      addActivity(`Etkinlik güncellendi: ${payload.title}`, payload.id);
    } else {
      setLastAction({ events: cloneEvents(events), label: 'Created event' });
      setEvents((prev) => [...prev, payload]);
      addActivity(`Etkinlik oluşturuldu: ${payload.title}`, payload.id);
      const reminderTime = new Date(safeStart.getTime() - 15 * 60 * 1000);
      setReminders((prev) => [
        ...prev,
        {
          id: crypto.randomUUID(),
          eventId: payload.id,
          at: reminderTime.toISOString(),
          message: `Reminder: ${payload.title}`,
        },
      ]);
    }
    setIsModalOpen(false);
    setEditingId(null);
  };

  const handleDelete = () => {
    if (!editingId) return;
    const target = events.find((e) => e.id === editingId);
    setEvents((prev) => prev.filter((event) => event.id !== editingId));
    if (target) addActivity(`Etkinlik silindi: ${target.title}`, target.id);
    setIsModalOpen(false);
    setEditingId(null);
  };

  const renderEventChip = (event: EventWithSpan) => {
    const start = new Date(event.start);
    const end = new Date(event.end);
    const label = event.allDay ? 'All day' : `${formatTimeTz(start)} – ${formatTimeTz(end)}`;
    const spanLabel =
      event.spanTotal && event.spanTotal > 1
        ? `Day ${event.spanIndex} of ${event.spanTotal}`
        : null;
    const spanPercent =
      event.spanTotal && event.spanTotal > 1 && event.spanIndex
        ? Math.round((event.spanIndex / event.spanTotal) * 100)
        : null;
    const spanPosition =
      event.spanTotal && event.spanTotal > 1
        ? event.spanIndex === 1
          ? 'start'
          : event.spanIndex === event.spanTotal
          ? 'end'
          : 'middle'
        : 'single';
    const hasLeft = spanPosition === 'middle' || spanPosition === 'end';
    const hasRight = spanPosition === 'middle' || spanPosition === 'start';
    return (
      <button
        key={`${event.id}-${event.start}`}
        className={`event-chip ${resizeState?.id === event.id ? 'is-resizing' : ''} ${
          event.spanTotal && event.spanTotal > 1 ? 'multi-span' : ''
        } ${hasLeft ? 'has-left' : ''} ${hasRight ? 'has-right' : ''}`}
        style={{ background: event.color, ['--event-color' as string]: event.color }}
        draggable
        onDragStart={beginDrag(event)}
        onDragEnd={endDrag}
        title={recurrenceSummary(event) || event.title}
        onClick={(e) => {
          e.stopPropagation();
          openForEdit(event);
        }}
      >
        <span className="event-chip-time">{label}</span>
        <span className="event-chip-title">{event.title}</span>
        {(event.location || event.onlineLink) && (
          <div className="event-chip-meta">
            {event.location && <span className="chip muted">{event.location}</span>}
            {event.onlineLink && (
              <a
                className="chip link"
                href={event.onlineLink}
                target="_blank"
                rel="noreferrer"
                onClick={(e) => e.stopPropagation()}
              >
                Join
              </a>
            )}
          </div>
        )}
        {event.attachments && event.attachments.length > 0 && (
          <div className="event-chip-meta">
            {event.attachments.map((att) => (
              <a
                key={att.id}
                className="chip link outline"
                href={att.url}
                target="_blank"
                rel="noreferrer"
                onClick={(e) => e.stopPropagation()}
              >
                {att.label}
              </a>
            ))}
          </div>
        )}
        {event.recurrence !== 'none' && (
          <span className="event-chip-repeat" aria-label="Repeats">
            ↻ {event.recurrence}
          </span>
        )}
        {event.meetingNotes && (
          <div className="event-chip-notes">
            <span className="chip muted">Notlar</span> {event.meetingNotes}
          </div>
        )}
        {event.checklist && event.checklist.length > 0 && (
          <div className="event-chip-checklist">
            {event.checklist.slice(0, 3).map((item) => (
              <label key={item.id} className="checklist-row">
                <input
                  type="checkbox"
                  checked={item.done}
                  onChange={(e) => {
                    e.stopPropagation();
                    toggleChecklistItem(event.id, item.id);
                  }}
                />
                <span className={item.done ? 'done' : ''}>{item.text}</span>
              </label>
            ))}
            {event.checklist.length > 3 && (
              <span className="chip muted small">+{event.checklist.length - 3} more</span>
            )}
          </div>
        )}
        {spanLabel && <span className="event-chip-span">{spanLabel}</span>}
        {spanPercent !== null && (
          <div className="event-span-meter">
            <div className="event-span-meter-fill" style={{ width: `${spanPercent}%` }} />
          </div>
        )}
        {event.recurrence !== 'none' && (
          <button
            type="button"
            className="mini-skip"
            onClick={(e) => {
              e.stopPropagation();
              skipOccurrence(event);
            }}
          >
            Skip this date
          </button>
        )}
        {event.recurrence !== 'none' && (
          <button
            type="button"
            className="mini-secondary"
            onClick={(e) => {
              e.stopPropagation();
              detachOccurrence(event);
            }}
          >
            Detach occurrence
          </button>
        )}
        <div className="share-row">
          <button
            type="button"
            className="mini-secondary"
            onClick={(e) => {
              e.stopPropagation();
              copyShareLink(event);
            }}
          >
            Link kopyala
          </button>
          <button
            type="button"
            className="mini-secondary ghosty"
            onClick={(e) => {
              e.stopPropagation();
              openPublicPreview(event);
            }}
          >
            Salt okuma
          </button>
        </div>
        {!event.allDay && (
          <div className="resize-row">
            <span className="resize-handle" onMouseDown={startResize(event, 'start')}>
              ⇡
            </span>
            <span className="resize-handle" onMouseDown={startResize(event, 'end')}>
              ⇣
            </span>
          </div>
        )}
      </button>
    );
  };

  const renderAgendaItem = (item: EventWithSpan) => (
    <div
      key={`${item.id}-${item.start}`}
      className="agenda-item"
      draggable
      onDragStart={beginDrag(item)}
      onDragEnd={endDrag}
      title={recurrenceSummary(item) || item.title}
      onClick={() => openForEdit(item)}
    >
      <div className="agenda-time">
        {item.allDay
          ? 'All day'
          : `${formatTimeTz(new Date(item.start))} – ${formatTimeTz(new Date(item.end))}`}
      </div>
      <div className="agenda-body">
        <div className="agenda-title" style={{ color: item.color }}>
          {item.title}
        </div>
        {(item.location || item.onlineLink) && (
          <div className="agenda-meta">
            {item.location && <span className="chip muted">{item.location}</span>}
            {item.onlineLink && (
              <a
                className="chip link"
                href={item.onlineLink}
                target="_blank"
                rel="noreferrer"
                onClick={(e) => e.stopPropagation()}
              >
                Join link
              </a>
            )}
          </div>
        )}
        {item.attendees && item.attendees.length > 0 && (
          <div className="agenda-attendees">
            {item.attendees.map((person) => (
              <span
                key={person.id}
                className={`chip attendee-chip ${person.status}`}
                title={rsvpLabel[person.status]}
              >
                {person.name} • {rsvpLabel[person.status]}
              </span>
            ))}
          </div>
        )}
        {item.spanTotal && item.spanTotal > 1 && (
          <div className="agenda-span">
            Day {item.spanIndex} of {item.spanTotal}
          </div>
        )}
        {item.recurrence !== 'none' && <div className="agenda-repeat">Repeats {item.recurrence}</div>}
        {item.description && <div className="agenda-description">{item.description}</div>}
        {item.attachments && item.attachments.length > 0 && (
          <div className="agenda-attachments">
            {item.attachments.map((att) => (
              <a
                key={att.id}
                className="chip link outline"
                href={att.url}
                target="_blank"
                rel="noreferrer"
              >
                {att.label}
              </a>
            ))}
            </div>
          )}
        {item.meetingNotes && (
          <div className="agenda-description muted">
            <strong>Notlar: </strong>
            {item.meetingNotes}
          </div>
        )}
        {item.checklist && item.checklist.length > 0 && (
          <div className="agenda-checklist">
            {item.checklist.map((task) => (
              <label key={task.id} className="checklist-row">
                <input
                  type="checkbox"
                  checked={task.done}
                  onChange={(e) => {
                    e.stopPropagation();
                    toggleChecklistItem(item.id, task.id);
                  }}
                />
                <span className={task.done ? 'done' : ''}>{task.text}</span>
              </label>
            ))}
          </div>
        )}
      </div>
      {!item.allDay && (
        <div className="agenda-handles">
          <button className="resize-handle" onMouseDown={startResize(item, 'start')}>
            Start ⇡
          </button>
          <button className="resize-handle" onMouseDown={startResize(item, 'end')}>
            End ⇣
          </button>
        </div>
      )}
      {item.recurrence !== 'none' && (
        <div className="agenda-actions">
          <button className="mini-skip" onClick={() => skipOccurrence(item)}>
            Skip this date
          </button>
          <button className="mini-secondary" onClick={() => detachOccurrence(item)}>
            Detach occurrence
          </button>
        </div>
      )}
    </div>
  );

  const VirtualEventList: React.FC<{
    items: EventWithSpan[];
    renderItem: (item: EventWithSpan) => React.ReactElement;
    rowHeight?: number;
    overscan?: number;
    maxHeight?: string;
    className?: string;
    empty?: React.ReactNode;
  }> = ({
    items,
    renderItem,
    rowHeight = 120,
    overscan = 4,
    maxHeight = '60vh',
    className = '',
    empty = null,
  }) => {
    const { containerRef, visibleItems, paddingTop, paddingBottom } = useVirtualList(items, {
      rowHeight,
      overscan,
    });
    if (items.length === 0) return <>{empty}</>;
    const shouldVirtualize = items.length >= overscan * 2 + 8;
    if (!shouldVirtualize) {
      return <div className={`virtual-window ${className}`}>{items.map(renderItem)}</div>;
    }

    return (
      <div
        ref={containerRef}
        className={`virtual-scroll ${className}`}
        style={{ maxHeight }}
        role="list"
        aria-label="Virtualized events"
      >
        <div style={{ paddingTop, paddingBottom }} className="virtual-window">
          {visibleItems.map(renderItem)}
        </div>
      </div>
    );
  };

  const renderMonthView = () => (
    <div className="month-view">
      <div className="day-label-row">
        {daysOfWeek.map((day) => (
          <div key={day} className="day-label">
            {day}
          </div>
        ))}
      </div>
      <div className="calendar-grid">
        {monthDays.map((day) => {
          const dayEvents = eventsForDay(day);
          const dayKey = toInputDate(day);
          const heatCount = monthHeat.map[dayKey] ?? 0;
          const intensity = heatCount === 0 ? 0 : Math.min(1, heatCount / monthHeat.max);
          const heatStyle =
            filters.heatmap && intensity > 0
              ? {
                  backgroundColor: withAlpha(activeTheme.nowLine || '#ef4444', 0.08 + intensity * 0.22),
                }
              : undefined;
          return (
            <div
              key={day.toISOString()}
              className={`day-cell ${isSameMonth(day, currentDate) ? '' : 'muted-day'} ${
                isSameDay(day, new Date()) ? 'today' : ''
              } ${hoveredDay === day.toISOString() ? 'drag-hover' : ''}`}
              onClick={() => setCurrentDate(day)}
              onDragOver={handleDragOver}
              onDrop={handleDropOnDay(day)}
              onDragEnter={handleDragEnterDay(day)}
              onDragLeave={handleDragLeaveDay(day)}
            >
              <div className="day-heat" style={heatStyle} />
              <div className="day-content">
                <div className="day-cell-header">
                  <span className="day-number">{day.getDate()}</span>
                  <button
                    className="icon-button"
                    aria-label="Add event"
                    onClick={(e) => {
                      e.stopPropagation();
                      openNewEvent(day);
                    }}
                  >
                    +
                  </button>
                </div>
                <div className="event-stack">
                  {dayEvents.length === 0 ? (
                    <div className="empty-slot">No events</div>
                  ) : (
                    dayEvents.map((item) => renderEventChip(item))
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );

  const renderWeekView = () => (
    <div className="week-view">
      <div className="week-columns">
        {weekDays.map((day) => (
          <div
            key={day.toISOString()}
            className={`week-day ${isSameDay(day, new Date()) ? 'today' : ''} ${
              hoveredDay === day.toISOString() ? 'drag-hover' : ''
            }`}
            onClick={() => setCurrentDate(day)}
            onDragOver={handleDragOver}
            onDrop={handleDropOnDay(day)}
            onDragEnter={handleDragEnterDay(day)}
            onDragLeave={handleDragLeaveDay(day)}
          >
            <div className="week-day-header">
              <div>
                <div className="week-day-name">{daysOfWeek[day.getDay()]}</div>
                <div className="week-day-date">
                  {day.getDate()} {day.toLocaleString('en-US', { month: 'short' })}
                </div>
              </div>
              <button
                className="mini-action"
                onClick={(e) => {
                  e.stopPropagation();
                  openNewEvent(day);
                }}
              >
                New
              </button>
            </div>
            <div className="event-column">
              <VirtualEventList
                items={eventsForDay(day)}
                renderItem={renderEventChip}
                rowHeight={118}
                overscan={6}
                maxHeight="65vh"
                empty={<div className="empty-slot">Nothing scheduled</div>}
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderTimelineView = () => {
    const weekDayEvents = weekDays.map((day) => ({
      day,
      events: eventsForDay(day),
    }));

    return (
      <div className="timeline-view">
        <div className="timeline-grid">
          <div className="timeline-time-col" />
          {weekDayEvents.map(({ day }) => (
            <div key={day.toISOString()} className="timeline-day-header">
              <div className="week-day-name">{daysOfWeek[day.getDay()]}</div>
              <div className="week-day-date">
                {day.getDate()} {day.toLocaleString('en-US', { month: 'short' })}
              </div>
            </div>
          ))}
          <div className="timeline-all-day-label">All day</div>
          {weekDayEvents.map(({ day, events }) => {
            const allDayEvents = events.filter((e) => e.allDay);
            return (
              <div key={`${day.toISOString()}-all`} className="timeline-all-day-cell">
                {allDayEvents.length === 0 && <div className="empty-slot">—</div>}
                {allDayEvents.map((item) => renderEventChip(item))}
              </div>
            );
          })}
          {timelineHours.map((hour) => (
            <React.Fragment key={hour}>
              <div className="timeline-time-col">{`${pad(hour)}:00`}</div>
              {weekDayEvents.map(({ day, events }) => {
                const dayEvents = events.filter((e) => !e.allDay);
                const slotStart = new Date(day);
                slotStart.setHours(hour, 0, 0, 0);
                const slotEnd = new Date(day);
                slotEnd.setHours(hour + 1, 0, 0, 0);
                const slotEvents = dayEvents.filter(
                  (e) => new Date(e.start) < slotEnd && new Date(e.end) > slotStart
                );
                return (
                  <div key={`${day.toISOString()}-${hour}`} className="timeline-cell">
                    {slotEvents.map((item) => (
                      <div key={`${item.id}-${item.start}`} className="timeline-event">
                        <div className="timeline-event-title" style={{ color: item.color }}>
                          {item.title}
                        </div>
                        <div className="timeline-event-time">
                          {formatTimeTz(new Date(item.start))} – {formatTimeTz(new Date(item.end))}
                        </div>
                      </div>
                    ))}
                  </div>
                );
              })}
            </React.Fragment>
          ))}
        </div>
      </div>
    );
  };

  const renderDayView = () => {
    const dayEvents = eventsForDay(currentDate);
    const isHovering = hoveredDay === currentDate.toISOString();
    return (
      <div
        className={`day-view ${isHovering ? 'drag-hover' : ''}`}
        onDragOver={handleDragOver}
        onDrop={handleDropOnDay(currentDate)}
        onDragEnter={handleDragEnterDay(currentDate)}
        onDragLeave={handleDragLeaveDay(currentDate)}
      >
        <div className="day-view-header">
          <div>
            <div className="day-title">
              {currentDate.toLocaleDateString('en-US', {
                weekday: 'long',
                month: 'long',
                day: 'numeric',
              })}
            </div>
            <div className="day-subtitle">{dayEvents.length} events</div>
          </div>
          <button className="pill primary" onClick={() => openNewEvent(currentDate)}>
            Add event
          </button>
        </div>
        <div className="day-agenda">
          {dayEvents.length === 0 && <div className="empty-slot prominent">Your day is clear</div>}
          {dayEvents.length > 0 && (
            <VirtualEventList
              items={dayEvents}
              renderItem={renderAgendaItem}
              rowHeight={150}
              overscan={8}
              maxHeight="70vh"
              className="agenda-virtual"
            />
          )}
        </div>
      </div>
    );
  };

  return (
    <div className={`app-shell theme-${themeId}`}>
      <header className="calendar-header">
        <div className="brand">
          <div className="brand-mark">LC</div>
          <div>
            <div className="brand-title">Lodos Calendar</div>
            <div className="brand-subtitle">Plan like a pro, stay effortlessly aligned</div>
          </div>
        </div>
        <div className="controls">
          <div className="navigation">
            <button className="pill ghost" onClick={() => setCurrentDate(new Date())}>
              Today
            </button>
            <div className="nav-month">
              <button className="icon-button" onClick={handlePrev} aria-label="Previous period">
                ←
              </button>
              <span className="nav-label">{formatMonthYear(currentDate)}</span>
              <button className="icon-button" onClick={handleNext} aria-label="Next period">
                →
              </button>
            </div>
          </div>
          <div className="header-actions">
            {activeFilterCount > 0 && (
              <div className="filter-badge" title="Active filters">
                Filters {activeFilterCount}
              </div>
            )}
            <button className="pill ghost kbd-button" onClick={() => setIsShortcutsOpen(true)}>
              <span className="kbd-icon">⌘</span>
              Shortcuts
            </button>
            <label className="theme-picker">
              <span className="sr-only">Tema</span>
              <select value={themeId} onChange={(e) => setThemeId(e.target.value as ThemeId)}>
                {themePresets.map((theme) => (
                  <option key={theme.id} value={theme.id}>
                    {theme.label}
                  </option>
                ))}
                <option value="custom">Özel</option>
              </select>
            </label>
            <button
              className="pill ghost"
              onClick={() => {
                if (fileInputRef.current) fileInputRef.current.click();
              }}
            >
              Import .ics
            </button>
            <button className="pill ghost" onClick={exportICS}>
              Export .ics
            </button>
            {lastAction && (
              <button className="pill ghost" onClick={undoLastChange}>
                Undo {lastAction.label}
              </button>
            )}
          </div>
          <div className="view-switcher">
            {(['month', 'week', 'timeline', 'day'] as CalendarView[]).map((item) => (
              <button
                key={item}
                className={`pill ghost ${view === item ? 'active' : ''}`}
                onClick={() => setView(item)}
              >
                {item.charAt(0).toUpperCase() + item.slice(1)}
                {view === item && activeFilterCount > 0 && (
                  <span className="filter-count">({currentFilteredCount})</span>
                )}
              </button>
            ))}
            <button className="pill primary" onClick={() => openNewEvent(currentDate)}>
              + New event
            </button>
          </div>
        </div>
      </header>
      <input
        ref={fileInputRef}
        type="file"
        accept=".ics,text/calendar"
        style={{ display: 'none' }}
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) handleImportICS(file);
          e.target.value = '';
        }}
      />

      {isShortcutsOpen && (
        <div className="modal-backdrop" onClick={() => setIsShortcutsOpen(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div>
                <div className="modal-title">
                  <span className="kbd-icon">⌘</span> Keyboard shortcuts
                </div>
              <div className="modal-subtitle">Stay quick with a few handy keys</div>
              </div>
              <button className="icon-button" onClick={() => setIsShortcutsOpen(false)}>
                ×
              </button>
            </div>
            <div className="modal-body">
              {[
                ['T', 'Jump to today'],
                ['N', 'New event'],
                ['← / →', 'Previous / Next'],
                ['1', 'Day view'],
                ['2', 'Week view'],
                ['3', 'Month view'],
              ].map(([key, action]) => (
                <div key={key} className="shortcut-row">
                  <span className="kbd-icon">{key}</span>
                  <span>{action}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      <main className="calendar-content">
        <section className="primary-panel">
          {view === 'month' && renderMonthView()}
          {view === 'week' && renderWeekView()}
          {view === 'timeline' && renderTimelineView()}
          {view === 'day' && renderDayView()}
        </section>
        <aside className="sidebar">
          <div className="sidebar-card">
            <div className="sidebar-title">Preferences</div>
            <div className="sidebar-subtitle">Timezone and working hours</div>
            <div className="filters">
              <label className="field">
                <span>Timezone</span>
                <select value={timezone} onChange={(e) => setTimezone(e.target.value)}>
                  {timezoneOptions.map((tz) => (
                    <option key={tz} value={tz}>
                      {tz}
                    </option>
                  ))}
                </select>
              </label>
              <label className="field">
                <span>Tema</span>
                <select value={themeId} onChange={(e) => setThemeId(e.target.value as ThemeId)}>
                  {themePresets.map((theme) => (
                    <option key={theme.id} value={theme.id}>
                      {theme.label} • {theme.description}
                    </option>
                  ))}
                  <option value="custom">Özel tema</option>
                </select>
                <div className="sidebar-subtitle" style={{ marginTop: 4 }}>
                  Arayüz rengini hızlıca değiştirin. Özel seçtiğinizde aşağıda renkler açılır.
                </div>
              </label>
              {themeId === 'custom' && (
                <div className="theme-grid">
                  {customThemeFields.map(({ key, label }) => (
                    <label key={key} className="field">
                      <span>{label}</span>
                      <input
                        type="color"
                        value={customTheme[key]}
                        onChange={(e) => updateCustomTheme(key, e.target.value)}
                      />
                    </label>
                  ))}
                </div>
              )}
              <div className="field-row">
                <label className="field">
                  <span>Work start</span>
                  <input
                    type="time"
                    value={`${pad(workHours.start)}:00`}
                    onChange={(e) =>
                      setWorkHours((prev) => {
                        const nextStart = parseHour(e.target.value, prev.start);
                        return { ...prev, start: Math.min(nextStart, prev.end - 1) };
                      })
                    }
                  />
                </label>
                <label className="field">
                  <span>Work end</span>
                  <input
                    type="time"
                    value={`${pad(workHours.end)}:00`}
                    onChange={(e) =>
                      setWorkHours((prev) => {
                        const nextEnd = parseHour(e.target.value, prev.end);
                        return { ...prev, end: Math.max(nextEnd, prev.start + 1) };
                      })
                    }
                  />
                </label>
              </div>
            </div>
          </div>
          <div className="sidebar-card">
            <div className="sidebar-title">Summary</div>
            <div className="sidebar-subtitle">Quick analytics for your {summaryRange}</div>
            <div className="summary-range">
              {(['week', 'month'] as const).map((range) => (
                <button
                  key={range}
                  className={`pill ghost ${summaryRange === range ? 'active' : ''}`}
                  onClick={() => setSummaryRange(range)}
                >
                  {range === 'week' ? 'This week' : 'This month'}
                </button>
              ))}
            </div>
            <div className="summary-grid">
              <div className="summary-card">
                <div className="summary-label">Meetings</div>
                <div className="summary-value">{summary.meetingCount}</div>
              </div>
              <div className="summary-card">
                <div className="summary-label">All day</div>
                <div className="summary-value">{summary.allDayCount}</div>
              </div>
              <div className="summary-card">
                <div className="summary-label">Focus</div>
                <div className="summary-value">{summary.focusCount}</div>
              </div>
              <div className="summary-card">
                <div className="summary-label">Hours</div>
                <div className="summary-value">{summary.totalHours}h</div>
              </div>
            </div>
            <div className="sidebar-subtitle">Rapor dışa aktar</div>
            <div className="filter-actions">
              <button className="pill ghost" onClick={exportRangeCSV}>
                {summaryRange === 'week' ? 'Haftalık CSV' : 'Aylık CSV'}
              </button>
              <button className="pill primary" onClick={exportRangePDF}>
                {summaryRange === 'week' ? 'Haftalık PDF' : 'Aylık PDF'}
              </button>
            </div>
          </div>
          <div className="sidebar-card">
            <div className="sidebar-title">Upcoming</div>
            <div className="sidebar-subtitle">Quick glance at the next few days</div>
            <div className="upcoming-list">
              {upcoming.length === 0 && <div className="empty-slot prominent">Nothing scheduled</div>}
              {upcoming.map((event) => {
                const start = new Date(event.start);
                return (
                  <div
                    key={`${event.id}-${event.start}`}
                    className="upcoming-item"
                    title={recurrenceSummary(event) || event.title}
                    onClick={() => {
                      setCurrentDate(start);
                      setView('day');
                    }}
                  >
                    <div className="upcoming-date">
                      <span className="upcoming-day">{pad(start.getDate())}</span>
                      <span className="upcoming-month">
                        {start.toLocaleString('en-US', { month: 'short' })}
                      </span>
                    </div>
                    <div className="upcoming-meta">
                      <div className="upcoming-title" style={{ color: event.color }}>
                        {event.title}
                      </div>
                      {(event.location || event.onlineLink) && (
                        <div className="agenda-meta">
                          {event.location && <span className="chip muted">{event.location}</span>}
                          {event.onlineLink && (
                            <a
                              className="chip link"
                              href={event.onlineLink}
                              target="_blank"
                              rel="noreferrer"
                              onClick={(e) => e.stopPropagation()}
                            >
                              Join
                            </a>
                          )}
                        </div>
                      )}
                      <div className="upcoming-time">
                        {event.allDay
                          ? 'All day'
                          : `${formatTimeTz(new Date(event.start))} – ${formatTimeTz(
                              new Date(event.end)
                            )}`}
                      </div>
                      {event.attendees && event.attendees.length > 0 && (
                        <div className="agenda-attendees">
                          {event.attendees.map((person) => (
                            <span key={person.id} className={`chip attendee-chip ${person.status}`}>
                              {person.name}
                            </span>
                          ))}
                        </div>
                      )}
                      {event.attachments && event.attachments.length > 0 && (
                        <div className="agenda-attachments">
                          {event.attachments.map((att) => (
                            <a
                              key={att.id}
                              className="chip link outline"
                              href={att.url}
                              target="_blank"
                              rel="noreferrer"
                              onClick={(e) => e.stopPropagation()}
                            >
                              {att.label}
                            </a>
                          ))}
                        </div>
                      )}
                      {event.recurrence !== 'none' && (
                        <div className="agenda-repeat">Repeats {event.recurrence}</div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          <div className="sidebar-card">
            <div className="sidebar-title">Reminders</div>
            <div className="sidebar-subtitle">Alerts scheduled for your events</div>
            <div className="upcoming-list">
              {pendingReminders.length === 0 && (
                <div className="empty-slot prominent">No upcoming reminders</div>
              )}
              {pendingReminders.map((reminder) => {
                const event = events.find((e) => e.id === reminder.eventId);
                const when = new Date(reminder.at);
                return (
                  <div key={reminder.id} className="upcoming-item">
                    <div className="upcoming-date">
                      <span className="upcoming-day">{pad(when.getDate())}</span>
                      <span className="upcoming-month">
                        {when.toLocaleString('en-US', { month: 'short' })}
                      </span>
                    </div>
                    <div className="upcoming-meta">
                      <div className="upcoming-title">{reminder.message}</div>
                      <div className="upcoming-time">
                        {formatTimeTz(when)}
                      </div>
                      {event && (
                        <>
                          {event.location && (
                            <div className="agenda-attendees">Location: {event.location}</div>
                          )}
                          {event.onlineLink && (
                            <a
                              className="chip link"
                              href={event.onlineLink}
                              target="_blank"
                              rel="noreferrer"
                            >
                              Join link
                            </a>
                          )}
                        </>
                      )}
                      {event && (
                        <button
                          className="pill ghost"
                          onClick={() => {
                            setCurrentDate(new Date(event.start));
                            setView('day');
                          }}
                        >
                          View event
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          <div className="sidebar-card">
            <div className="sidebar-title">Activity</div>
            <div className="sidebar-subtitle">Kim, ne zaman, ne yaptı</div>
            <div className="activity-list">
              {activityLog.length === 0 && (
                <div className="empty-slot prominent">Henüz log yok</div>
              )}
              {activityLog.slice(0, 15).map((log) => {
                const when = new Date(log.at);
                return (
                  <div key={log.id} className="activity-row">
                    <div className="activity-dot" />
                    <div className="activity-body">
                      <div className="activity-message">{log.message}</div>
                      <div className="activity-time">
                        {formatDate(when)} · {formatTimeTz(when)}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          <div className="sidebar-card">
            <div className="sidebar-title">Filters</div>
            <div className="sidebar-subtitle">Narrow events by title, color, date range</div>
            <div className="filters">
              <label className="field">
                <span>Search</span>
                <input
                  type="text"
                  value={filters.query}
                  onChange={(e) => setFilters({ ...filters, query: e.target.value })}
                  placeholder="Title or description"
                />
              </label>
              <div className="field">
                <span>Color</span>
                <div className="color-chips">
                  <button
                    className={`color-chip ${filters.color === '' ? 'active' : ''}`}
                    onClick={() => setFilters({ ...filters, color: '' })}
                  >
                    Any
                  </button>
                  {colorOptions.map((color) => (
                    <button
                      key={color}
                      className={`color-chip ${filters.color === color ? 'active' : ''}`}
                      style={{ background: color }}
                      onClick={() =>
                        setFilters((prev) => ({ ...prev, color: prev.color === color ? '' : color }))
                      }
                      aria-label={`Filter by ${color}`}
                    />
                  ))}
                </div>
              </div>
              <label className="field">
                <span>Location contains</span>
                <input
                  type="text"
                  value={filters.location}
                  onChange={(e) => setFilters({ ...filters, location: e.target.value })}
                  placeholder="Office, cafe, etc."
                />
              </label>
              <label className="field">
                <span>Attendee contains</span>
                <input
                  type="text"
                  value={filters.attendee}
                  onChange={(e) => setFilters({ ...filters, attendee: e.target.value })}
                  placeholder="Alice, Bob..."
                />
              </label>
              <label className="field">
                <span>Online link contains</span>
                <input
                  type="text"
                  value={filters.onlineLink}
                  onChange={(e) => setFilters({ ...filters, onlineLink: e.target.value })}
                  placeholder="meet, zoom, teams..."
                />
              </label>
              <div className="field">
                <span>Calendars</span>
                <div className="color-chips">
                  {calendars.map((cal) => (
                    <button
                      key={cal.id}
                      className={`color-chip ${filters.calendars.includes(cal.id) ? 'active' : ''}`}
                      onClick={() =>
                        setFilters((prev) => {
                          const has = prev.calendars.includes(cal.id);
                          const next = has
                            ? prev.calendars.filter((c) => c !== cal.id)
                            : [...prev.calendars, cal.id];
                          return { ...prev, calendars: next.length ? next : calendars.map((c) => c.id) };
                        })
                      }
                    >
                      {cal.label}
                    </button>
                  ))}
                </div>
              </div>
              <div className="field-row">
                <label className="field">
                  <span>From</span>
                  <input
                    type="date"
                    value={filters.from}
                    onChange={(e) => setFilters({ ...filters, from: e.target.value })}
                  />
                </label>
                <label className="field">
                  <span>To</span>
                  <input
                    type="date"
                    value={filters.to}
                    onChange={(e) => setFilters({ ...filters, to: e.target.value })}
                  />
                </label>
              </div>
              <label className="checkbox">
                <input
                  type="checkbox"
                  checked={Boolean(filters.heatmap)}
                  onChange={(e) => setFilters({ ...filters, heatmap: e.target.checked })}
                />
                Heatmap (ay görünümünde yoğunluk)
              </label>
              {conflictWarning && <div className="conflict-warning">{conflictWarning}</div>}
              <div className="filter-actions">
                <button
                  className="pill ghost"
                  onClick={() =>
                    setFilters({
                      query: '',
                      color: '',
                      from: '',
                      to: '',
                      name: '',
                      calendars: calendars.map((c) => c.id),
                      location: '',
                      attendee: '',
                      onlineLink: '',
                    })
                  }
                >
                  Clear filters
                </button>
                <button
                  className="pill primary"
                  onClick={() => {
                    if (!filters.name) {
                      const name = prompt('Preset name?') || '';
                      if (!name.trim()) return;
                      setFilters({ ...filters, name });
                      setSavedFilters((prev) => [...prev, { ...filters, name }]);
                    } else {
                      setSavedFilters((prev) => [...prev, filters]);
                    }
                  }}
                >
                  Save preset
                </button>
              </div>
              {savedFilters.length > 0 && (
                <div className="presets">
                  <div className="sidebar-subtitle">Presets</div>
                  <div className="preset-chips">
                    {savedFilters.map((preset, index) => (
                      <button
                        key={`${preset.name}-${index}`}
                        className="color-chip"
                        onClick={() => setFilters({ ...preset })}
                        title={`Query: ${preset.query || '—'}, Color: ${preset.color || 'Any'}`}
                      >
                        {preset.name || 'Preset'}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
          <div className="sidebar-card highlight">
            <div className="sidebar-title">Add something new</div>
            <div className="sidebar-subtitle">
              Jump into the planner to block focus time, invite teammates, or note a reminder.
            </div>
            <button className="pill ghost full-width" onClick={() => openNewEvent(currentDate)}>
              Create event
            </button>
          </div>
        </aside>
      </main>

      {isModalOpen && (
        <div className="modal-backdrop" onClick={() => setIsModalOpen(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div>
                <div className="modal-title">
                  {editingId ? 'Edit event' : 'New event'} · {form.date}
                </div>
              <div className="modal-subtitle">Keep details light; you can refine later.</div>
              </div>
              <button className="icon-button" onClick={() => setIsModalOpen(false)}>
                ×
              </button>
            </div>
            <form className="modal-body" onSubmit={handleSubmit}>
              <div className="template-chip-row">
                {eventTemplates.map((template) => (
                  <button
                    key={template.id}
                    type="button"
                    className={`template-chip ${lastTemplateId === template.id ? 'active' : ''}`}
                    onClick={() => applyTemplate(template)}
                    title={template.description}
                  >
                    <span className="template-name">{template.name}</span>
                    <span className="template-desc">{template.description}</span>
                  </button>
                ))}
              </div>
              <label className="field">
                <span>Title</span>
                <input
                  type="text"
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  placeholder="What are we planning?"
                />
                {formErrors.title && <span className="error-text">{formErrors.title}</span>}
              </label>
              <div className="field-row">
                <label className="field">
                  <span>Date</span>
                  <input
                    type="date"
                    value={form.date}
                    onChange={(e) => setForm({ ...form, date: e.target.value })}
                  />
                  {formErrors.date && <span className="error-text">{formErrors.date}</span>}
                </label>
                <label className="field">
                  <span>Color</span>
                  <input
                    type="color"
                    value={form.color}
                    onChange={(e) => setForm({ ...form, color: e.target.value })}
                  />
                </label>
                <label className="field">
                  <span>Calendar</span>
                  <select
                    value={form.calendarId}
                    onChange={(e) => setForm({ ...form, calendarId: e.target.value as CalendarId })}
                  >
                    {calendars.map((cal) => (
                      <option key={cal.id} value={cal.id}>
                        {cal.label}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="field">
                  <span>Repeat</span>
                  <select
                    value={form.repeat}
                    onChange={(e) => setForm({ ...form, repeat: e.target.value as Recurrence })}
                  >
                    <option value="none">Does not repeat</option>
                    <option value="daily">Every day</option>
                    <option value="weekly">Every week</option>
                    <option value="monthly">Every month</option>
                  </select>
                </label>
                {form.repeat !== 'none' && (
                  <label className="field">
                    <span>Repeat until</span>
                    <input
                      type="date"
                      value={form.repeatUntil}
                      onChange={(e) => setForm({ ...form, repeatUntil: e.target.value })}
                      min={form.date}
                    />
                  </label>
                )}
              </div>
              <div className="field-row">
                <label className="field">
                  <span>Location</span>
                  <input
                    type="text"
                    value={form.location}
                    onChange={(e) => setForm({ ...form, location: e.target.value })}
                    placeholder="Office, venue, or address"
                  />
                </label>
                <label className="field">
                  <span>Katılımcılar · RSVP</span>
                  <div className="attendee-editor">
                    <div className="attendee-add-row">
                      <input
                        type="text"
                        value={form.newAttendeeName}
                        onChange={(e) => setForm({ ...form, newAttendeeName: e.target.value })}
                        placeholder="İsim ekle"
                      />
                      <select
                        value={form.newAttendeeStatus}
                        onChange={(e) =>
                          setForm({
                            ...form,
                            newAttendeeStatus: e.target.value as RSVPStatus,
                          })
                        }
                      >
                        <option value="accepted">Kabul</option>
                        <option value="maybe">Belki</option>
                        <option value="declined">Reddetti</option>
                      </select>
                      <button type="button" className="pill primary" onClick={addAttendeeToForm}>
                        Ekle
                      </button>
                    </div>
                    {attendeeSuggestions.length > 0 && (
                      <div>
                        <div className="suggestion-title">Son kullanılanlar</div>
                        <div className="suggestion-chips">
                          {attendeeSuggestions.map((name) => (
                            <button
                              key={name}
                              type="button"
                              className="suggestion-chip"
                              onClick={() => setForm((prev) => ({ ...prev, newAttendeeName: name }))}
                            >
                              {name}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                    {form.attendees.length > 0 && (
                      <div className="attendee-list">
                        {form.attendees.map((attendee) => (
                          <div key={attendee.id} className="attendee-row">
                            <span className="attendee-name">{attendee.name}</span>
                            <select
                              value={attendee.status}
                              onChange={(e) =>
                                updateAttendeeStatusInForm(
                                  attendee.id,
                                  e.target.value as RSVPStatus
                                )
                              }
                            >
                              <option value="accepted">Kabul</option>
                              <option value="maybe">Belki</option>
                              <option value="declined">Reddetti</option>
                            </select>
                            <button
                              type="button"
                              className="icon-button"
                              onClick={() => removeAttendeeFromForm(attendee.id)}
                              aria-label="Remove attendee"
                            >
                              ×
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </label>
              </div>
              <label className="field">
                <span>Online link</span>
                <input
                  type="url"
                  value={form.onlineLink}
                  onChange={(e) => setForm({ ...form, onlineLink: e.target.value })}
                  placeholder="https://meet.example.com/room"
                />
                {formErrors.onlineLink && <span className="error-text">{formErrors.onlineLink}</span>}
              </label>
              <div className="field-row">
                <label className="field">
                  <span>Start</span>
                  <input
                    type="time"
                    value={form.startTime}
                    disabled={form.allDay}
                    onChange={(e) => setForm({ ...form, startTime: e.target.value })}
                  />
                  {formErrors.startTime && <span className="error-text">{formErrors.startTime}</span>}
                </label>
                <label className="field">
                  <span>End</span>
                  <input
                    type="time"
                    value={form.endTime}
                    disabled={form.allDay}
                    onChange={(e) => setForm({ ...form, endTime: e.target.value })}
                  />
                  {formErrors.endTime && <span className="error-text">{formErrors.endTime}</span>}
                </label>
                <label className="checkbox">
                  <input
                    type="checkbox"
                    checked={form.allDay}
                    onChange={(e) => setForm({ ...form, allDay: e.target.checked })}
                  />
                  All day
                </label>
              </div>
              {formErrors.timeRange && <span className="error-text">{formErrors.timeRange}</span>}
              <label className="field">
                <span>Description</span>
                    <textarea
                      value={form.description}
                      onChange={(e) => setForm({ ...form, description: e.target.value })}
                  placeholder="Agenda, notes, links..."
                />
              </label>
              <label className="field">
                <span>Toplantı notları</span>
                <textarea
                  value={form.meetingNotes}
                  onChange={(e) => setForm({ ...form, meetingNotes: e.target.value })}
                  placeholder="Kararlar, özetler..."
                />
              </label>
              <label className="field">
                <span>İç check-list</span>
                <div className="attachment-editor">
                  <div className="attachment-add-row">
                    <input
                      type="text"
                      value={form.newChecklistText}
                      onChange={(e) => setForm({ ...form, newChecklistText: e.target.value })}
                      placeholder="Yapılacak madde"
                    />
                    <button type="button" className="pill primary" onClick={addChecklistItemToForm}>
                      Ekle
                    </button>
                  </div>
                  {form.checklist.length > 0 && (
                    <div className="attachment-list">
                      {form.checklist.map((task) => (
                        <div key={task.id} className="attachment-row">
                          <label className="checklist-row">
                            <input
                              type="checkbox"
                              checked={task.done}
                              onChange={() => toggleChecklistItemInForm(task.id)}
                            />
                            <span className={task.done ? 'done' : ''}>{task.text}</span>
                          </label>
                          <button
                            type="button"
                            className="icon-button"
                            onClick={() => removeChecklistItemFromForm(task.id)}
                            aria-label="Remove task"
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </label>
              <label className="field">
                <span>Notlar / Ekler</span>
                <div className="attachment-editor">
                  <div className="attachment-add-row">
                    <input
                      type="text"
                      value={form.newAttachmentLabel}
                      onChange={(e) => setForm({ ...form, newAttachmentLabel: e.target.value })}
                      placeholder="Dosya veya bağlantı adı"
                    />
                    <input
                      type="url"
                      value={form.newAttachmentUrl}
                      onChange={(e) => setForm({ ...form, newAttachmentUrl: e.target.value })}
                      placeholder="https://..."
                    />
                    <button type="button" className="pill primary" onClick={addAttachmentToForm}>
                      Ekle
                    </button>
                  </div>
                  {form.attachments.length > 0 && (
                    <div className="attachment-list">
                      {form.attachments.map((attachment) => (
                        <div key={attachment.id} className="attachment-row">
                          <div className="attachment-meta">
                            <span className="attachment-label">{attachment.label}</span>
                            <a
                              className="attachment-link"
                              href={attachment.url}
                              target="_blank"
                              rel="noreferrer"
                            >
                              {attachment.url}
                            </a>
                          </div>
                          <button
                            type="button"
                            className="icon-button"
                            onClick={() => removeAttachmentFromForm(attachment.id)}
                            aria-label="Remove attachment"
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </label>
              {editingId && (
                <div className="share-card">
                  <div>
                    <div className="share-title">Paylaş ve görüntüle</div>
                    <div className="share-subtitle">
                        Bu etkinliği salt okuma linkiyle paylaş veya önizle.
                      </div>
                    </div>
                      <div className="share-actions">
                        <button
                          type="button"
                          className="pill ghost"
                          onClick={() => {
                            const current = events.find((item) => item.id === editingId);
                            if (current) copyShareLink(current);
                          }}
                        >
                          Linki kopyala
                        </button>
                        <button
                          type="button"
                          className="pill primary"
                          onClick={() => {
                            const current = events.find((item) => item.id === editingId);
                            if (current) openPublicPreview(current);
                          }}
                        >
                          Public view aç
                        </button>
                      </div>
                    </div>
                  )}
                  <div className="modal-actions">
                    {editingId && (
                      <button type="button" className="pill ghost danger" onClick={handleDelete}>
                        Delete
                      </button>
                )}
                <div className="spacer" />
                <button type="button" className="pill ghost" onClick={() => setIsModalOpen(false)}>
                  Cancel
                </button>
                <button type="submit" className="pill primary">
                  {editingId ? 'Save changes' : 'Add event'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      {isPublicViewOpen && sharedViewEvent && (
        <div className="modal-backdrop" onClick={closePublicView}>
          <div className="modal public-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div>
                <div className="modal-title">Paylaşılan etkinlik · Salt okuma</div>
                <div className="modal-subtitle">
                  Bu görünüm düzenleme yapmadan sadece okuma için.
                </div>
              </div>
              <button className="icon-button" onClick={closePublicView}>
                ×
              </button>
            </div>
            <div className="modal-body share-view">
              <div className="share-pill" style={{ background: sharedViewEvent.color }}>
                {sharedViewEvent.calendarId.toUpperCase()}
              </div>
              <div className="share-heading">{sharedViewEvent.title}</div>
              <div className="share-range">{formatSharedRange(sharedViewEvent)}</div>
              {sharedViewEvent.location && (
                <div className="share-line">📍 {sharedViewEvent.location}</div>
              )}
              {sharedViewEvent.onlineLink && (
                <a
                  className="share-link"
                  href={sharedViewEvent.onlineLink}
                  target="_blank"
                  rel="noreferrer"
                >
                  Çevrimiçi katıl
                </a>
              )}
              {sharedViewEvent.attendees && sharedViewEvent.attendees.length > 0 && (
                <div className="share-attendees">
                  {sharedViewEvent.attendees.map((person) => (
                    <span
                      key={person.id}
                      className={`chip attendee-chip ${person.status}`}
                      title={rsvpLabel[person.status]}
                    >
                      {person.name} · {rsvpLabel[person.status]}
                    </span>
                  ))}
                </div>
              )}
              {sharedViewEvent.attachments && sharedViewEvent.attachments.length > 0 && (
                <div className="share-attachments">
                  {sharedViewEvent.attachments.map((att) => (
                    <a
                      key={att.id}
                      className="attachment-link"
                      href={att.url}
                      target="_blank"
                      rel="noreferrer"
                    >
                      {att.label}
                    </a>
                  ))}
                </div>
              )}
              {sharedViewEvent.description && (
                <div className="share-description">{sharedViewEvent.description}</div>
              )}
              <div className="share-actions inline">
                <button
                  className="pill ghost"
                  onClick={() => copyShareLink(sharedViewEvent)}
                  type="button"
                >
                  Linki kopyala
                </button>
                <button className="pill primary" type="button" onClick={closePublicView}>
                  Kapat
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {shareToast && (
        <div className="toast">
          <span>{shareToast}</span>
          <button className="icon-button" onClick={() => setShareToast(null)}>
            ×
          </button>
        </div>
      )}
    </div>
  );
};

export default App;
